// Inline product picks for articles that don't mention specific products.
// Each pick specifies which H2 to insert after (1-indexed) and product details.

// ISO date the prices below were last refreshed by scripts/paapi-refresh-prices.ts.
// The script rewrites this line on every run that changes a price, so the UI can
// show users "prices updated {date}". Unlike Directus products these are file-local,
// so this is the only freshness signal available for article picks.
export const ARTICLE_PRODUCT_PICKS_PRICES_UPDATED_AT = '2026-09-07'

import { amazonUrl } from '@/lib/affiliate'

// Stable Amazon product image CDN URLs. See lib/amazon-image for why the long
// edge is 500 and why the _AC_ autocrop flag is not used.
import { amazonImageUrl as amazonImg } from '@/lib/amazon-image'

// Season-aware picks: a pick may name the months it is in season and the product
// that takes its slot the rest of the year. See lib/seasonal-picks.
import type { SeasonallySwappable } from '@/lib/seasonal-picks'

export type PickBadgeTone = 'gold' | 'green' | 'blue' | 'purple' | 'orange' | 'slate'

export interface PickBadge {
  label: string                // display copy, free to be specific (e.g. "Best for large, thick lawns")
  tone?: PickBadgeTone          // defaults to 'green'
  rating?: number               // 0-5; rendered as stars when tier === 'verdict' (or when a rating is set)
  tier?: PickBadgeTier          // stable analytics/behavior key, DECOUPLED from the display label.
                                // Drives affiliate placement + star display. Omit → legacy label mapping.
}

// Stable badge semantics for analytics + rating display, independent of the (freely-editable) label.
// verdict → 'verdict-card' + stars; editor → 'editor-pick'; pick → 'callout'. See inline-product-callout.
export type PickBadgeTier = 'verdict' | 'editor' | 'pick'

export interface ArticleProductPick extends SeasonallySwappable<PickBadge> {
  name: string
  affiliateUrl: string
  price: string
  description: string
  insertAfterH2: number // 1-indexed: insert after the Nth H2
  imageUrl?: string // Amazon product image URL
  badge?: PickBadge
  // Short, human product label for the specific CTA ("See {shortName} on Amazon").
  // Omit → the component derives one from the first few words of `name`. Keep it
  // recognizable ("Andersons PGF Complete", not "Andersons") so the button names the
  // actual product per the growth playbook's specificity rule.
  shortName?: string
  // Set on ANY pick of a slug to make insertProductPickMarkers respect every
  // pick's configured insertAfterH2 boundary instead of snapping the earliest
  // pick to the near-top lead anchor. Use for posts whose intro/first section
  // already carries an in-content affiliate block (e.g. the generated geo
  // posts), where a lead-anchored card would collide with it.
  disableLeadAnchor?: boolean
}

export const ARTICLE_PRODUCT_PICKS: Record<string, ArticleProductPick[]> = {
  // === Fall/winter capture push (2026-07-17) ===
  // ASINs reused from PAAPI-verified siblings in this file (NightShift from
  // the retired how-to-overseed-lawn entry, WinterGuard from the Scotts 4-Step set). One pick per
  // post per the one-product-display rule.
  'overseeding-with-ryegrass-for-winter': [
    {
      name: 'NightShift Perennial Ryegrass Seed 25 lb',
      affiliateUrl: amazonUrl('B0D8V9K1ZD'),
      price: '140.00',
      description: 'Dark-color perennial ryegrass blend with fast germination for winter overseeding.',
      insertAfterH2: 3,
      imageUrl: amazonImg('81LO3QP5NPL'),
      badge: { label: 'Winter overseed pick', tone: 'green', tier: 'pick' },
      shortName: 'NightShift Ryegrass',
    },
  ],
  // 2026-09-16: the newsletter's fall issue links here as its main CTA, so the
  // post carries the same three products the issue shows, each in the section
  // it belongs to. ASINs, prices and images PAAPI-verified 2026-09-16; picked
  // from 60-day affiliate click data (Andersons #1 overall, Jonathan Green the
  // most-clicked seed, Southern Lawn Food the most-clicked warm-season feed).
  'fall-lawn-care-schedule-checklist': [
    {
      name: 'Jonathan Green Black Beauty Ultra Grass Seed, 7 lb',
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '43.99',
      description: 'The overseeding pick for the September repair window. Tall fescue, bluegrass and rye with a dark color and deep roots, sized for overseeding a typical cool-season lawn.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51t-01Q0ylL'),
      badge: { label: 'Overseeding pick', tone: 'green', tier: 'pick' },
      shortName: 'Black Beauty Ultra',
    },
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer with 7% Humic DG 5,000 sq ft',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The October feed for either lawn type. A balanced 16-4-8 in super-fine particles that spread evenly, with humic DG for root growth before winter.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'October feeding pick', tone: 'gold', tier: 'editor' },
      shortName: 'Andersons PGF Complete',
    },
    {
      name: 'Scotts Turf Builder WinterGuard Fall Lawn Food, 12.5 lb.',
      affiliateUrl: amazonUrl('B000I1M5OU'),
      price: '39.85',
      description: 'The November winterizer for cool-season lawns. Builds deep roots and stores nutrients for a stronger spring green-up.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51Uon3VMZZL'),
      badge: { label: 'Winterizer pick', tone: 'purple', tier: 'pick' },
      shortName: 'WinterGuard Fall',
    },
    {
      name: 'Scotts Turf Builder Southern Lawn Food, 42.18 lbs.',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '58.47',
      description: 'For warm-season lawns adjusting this schedule: Bermuda, St. Augustine, zoysia and centipede get their last feed 4 to 6 weeks before frost, then nothing until spring green-up.',
      insertAfterH2: 6,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'Warm-season last feed', tone: 'orange', tier: 'pick' },
      shortName: 'Southern Lawn Food',
    },
  ],
  // 2026-09-16: curated so the page stops drawing sprayers and surfactant from
  // the category fallback. The post's own affiliate block (Andersons, Milorganite,
  // REOTEMP) sits at the end of H2 #8, so these avoid that section. Milorganite is
  // out of stock on Amazon today and is left to the in-content block only.
  'when-to-fertilize-your-lawn-simple-timing-rules-for-every-grass-type': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer with 7% Humic DG 5,000 sq ft',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A slow-release 16-4-8 that fits the main spring and fall windows on either lawn type. Releases over about eight weeks instead of surging in week one.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Fits every window', tone: 'gold', tier: 'editor' },
      shortName: 'Andersons PGF Complete',
    },
    {
      name: 'Scotts Turf Builder WinterGuard Fall Lawn Food, 12.5 lb.',
      affiliateUrl: amazonUrl('B000I1M5OU'),
      price: '39.85',
      description: 'The late-fall winterizer this section describes: more quick-release nitrogen to load roots before the ground freezes, applied around the last mow.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51Uon3VMZZL'),
      badge: { label: 'Cool-season winterizer', tone: 'purple', tier: 'pick' },
      shortName: 'WinterGuard Fall',
    },
    {
      name: 'Scotts Turf Builder Southern Lawn Food, 42.18 lbs.',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '58.47',
      description: 'A 32-0-10 built for the warm-season windows in this section: nitrogen for the summer surge, potassium for the wind-down, no phosphorus.',
      insertAfterH2: 6,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'Warm-season feed', tone: 'orange', tier: 'pick' },
      shortName: 'Southern Lawn Food',
    },
    {
      name: 'REOTEMP K82-3 Soil Thermometer, 5 Inch Stem, Waterproof',
      affiliateUrl: amazonUrl('B07F93K8ZY'),
      price: '12.99',
      description: 'Every timing rule in this plan keys off soil temperature at 2 to 4 inches, not a date. A waterproof dial thermometer is the cheapest way to read it.',
      insertAfterH2: 7,
      imageUrl: amazonImg('21ZMzIf-j4L'),
      badge: { label: 'The trigger', tone: 'slate', tier: 'pick' },
      shortName: 'REOTEMP Soil Thermometer',
    },
  ],
  // === Scotts 4-Step program (2026 audit) ===
  // Curated so the sidebar shows the actual four Scotts step products instead of a
  // random category draw (a liquid feed + surfactant + seed were surfacing on a
  // branded 4-Step post). ASINs/prices/images PAAPI-verified 2026-07-12; GrubEx
  // subbed for SummerGuard (B09MF6DKN3), which had no buy-box price.
  'scotts-4-step-program-annual-schedule': [
    {
      name: 'Scotts Turf Builder Halts Crabgrass Preventer with Lawn Food, 5,000 sq. ft.',
      affiliateUrl: amazonUrl('B000I1M5NG'),
      price: '29.97',
      description: 'A spring pre-emergent and lawn food in one bag that stops crabgrass before it germinates while giving the lawn its first feeding.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51krAW5ExNL'),
      badge: { label: 'Step 1: Spring pre-emergent', tone: 'green', tier: 'pick' },
      shortName: 'Halts + Lawn Food',
    },
    {
      name: 'Scotts Turf Builder Triple Action1, 11.31 lb.',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: 'A three-in-one late-spring bag that kills broadleaf weeds, prevents crabgrass, and feeds the lawn in a single pass.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Step 2: Weed control + feed', tone: 'blue', tier: 'pick' },
      shortName: 'Triple Action',
    },
    {
      name: 'Scotts GrubEx1 Season Long Grub Killer, 5,000 sq. ft.',
      affiliateUrl: amazonUrl('B0050DV4ZW'),
      price: '29.47',
      description: 'A season-long grub preventer that stops grubs while they are young, before they chew through summer roots.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41vLRIqeMiL'),
      badge: { label: 'Step 3: Summer insect protection', tone: 'orange', tier: 'pick' },
      shortName: 'GrubEx',
    },
    {
      name: 'Scotts Turf Builder WinterGuard Fall Lawn Food, 12.5 lb.',
      affiliateUrl: amazonUrl('B000I1M5OU'),
      price: '39.87',
      description: 'A fall lawn food that builds deep roots and stores nutrients so the lawn greens up stronger next spring.',
      insertAfterH2: 6,
      imageUrl: amazonImg('51Uon3VMZZL'),
      badge: { label: 'Step 4: Fall winterizer feed', tone: 'purple', tier: 'pick' },
      shortName: 'WinterGuard Fall',
    },
    {
      // 2026-08-18 CTR pass: all four steps are granular products that assume a
      // calibrated broadcast spreader, but the page never showed one. ASIN/price/
      // image reused from the PAAPI-verified application-guide entries.
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Every step in this program is a granular bag with a Scotts spreader setting printed on it. One calibrated broadcast spreader covers all four applications a year.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader for All 4 Steps', tone: 'slate' },
    },
  ],
  // === Cool-season grass-types guide (2026 audit) ===
  // Curated so a "which cool-season grass" reader gets seed/starter/soil-test picks
  // instead of a random equipment draw (an EGO string trimmer was surfacing).
  // ASINs/prices/images PAAPI-verified 2026-07-12.
  'complete-guide-to-coolseason-grass-types': [
    {
      name: 'Pennington Smart Seed Sun & Shade Mix South, Tall Fescue Grass Seeds for Lawn, 20 lb',
      affiliateUrl: amazonUrl('B08QCNV9ZV'),
      price: '60.73',
      description: 'A cool-season tall fescue sun-and-shade seed blend that stays green on up to 30% less water once it is established.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41sZdWaDgSS'),
      badge: { label: 'Best cool-season seed', tone: 'gold', tier: 'verdict' },
      shortName: 'Pennington Smart Seed',
    },
    {
      name: 'Scotts Turf Builder Starter Food for New Grass, 15 lb.',
      affiliateUrl: amazonUrl('B00DK49DVA'),
      price: '34.97',
      description: 'A high-phosphorus starter fertilizer that drives fast root growth on newly seeded grass through its first few weeks.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51su2w24uqL'),
      badge: { label: 'Best starter fertilizer', tone: 'green', tier: 'editor' },
      shortName: 'Scotts Starter Food',
    },
    {
      // Keep the name identical to the site-canonical B084TSNR79 entry ("MySoil
      // Soil Test Kit"): resolveAffiliateProductName resolves this ASIN from
      // ARTICLE_PRODUCT_PICKS (not enrichment/catalog) first-write-wins, and this
      // entry sits first in the object, so a longer name here would shadow the
      // canonical one site-wide (and break affiliate-product-names.test.ts).
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'A mail-in soil test kit that reports pH and nutrient levels with tailored recommendations before you drop a single seed.',
      insertAfterH2: 4,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test before you seed', tone: 'blue', tier: 'pick' },
      shortName: 'MySoil Test Kit',
    },
  ],
  // === Simple Lawn Solutions liquid-fertilizer cluster (2026) ===
  // One curated card per spoke (the spokes also auto-link product mentions via the
  // affiliate catalog). All ASINs + prices + image ids PAAPI-verified 2026-06-27.
  // Chooser + schedule are multi-product and rely on auto-links (no single card).
  'simple-lawn-solutions-16-4-8-review': [
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'The balanced all-purpose liquid feed reviewed here: 16-4-8 with seaweed and humic acid, applied through the attached hose-end sprayer on any grass type.',
      insertAfterH2: 2,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best all-around liquid feed', tone: 'gold', tier: 'editor' },
    },
  ],
  'simple-lawn-solutions-review': [
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'The flagship and the one to start with: a balanced 16-4-8 all-purpose feed with seaweed and humic acid, sprayed on through the attached hose-end sprayer.',
      insertAfterH2: 2,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Overall', tone: 'gold' },
    },
  ],
  'best-liquid-iron-for-lawns': [
    {
      name: 'Simple Lawn Solutions Liquid Iron Darker Green (32 oz)',
      affiliateUrl: amazonUrl('B08Z4DTTJR'),
      price: '23.99',
      description: 'Chelated liquid iron that deep-greens any grass type without forcing growth. The straightforward pick for color and for correcting iron chlorosis.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51OgZKkTOPL'),
      badge: { label: 'Best for Color', tone: 'green' },
    },
  ],
  'best-high-nitrogen-liquid-lawn-fertilizer': [
    {
      name: 'Simple Lawn Solutions Maximum Green 28-0-0 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9Z9MQR'),
      price: '24.79',
      description: 'High-nitrogen 28-0-0 with a quick and slow-release blend for a fast green-up on any grass type, applied through the attached hose-end sprayer.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41drKSOTKCL'),
      badge: { label: 'Best High-Nitrogen', tone: 'blue' },
    },
  ],
  '15-0-15-liquid-fertilizer-phosphorus-free': [
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'Phosphorus-free 15-0-15 with high potassium for summer heat and stress hardiness on established lawns, and the right call in phosphorus-restricted areas.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41wW+L4asjL'),
      badge: { label: 'Best Phosphorus-Free', tone: 'green' },
    },
  ],
  '3-18-18-liquid-starter-fertilizer': [
    {
      name: 'Simple Lawn Solutions Ultimate 3-18-18 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XB32CVV'),
      price: '25.79',
      description: 'High-phosphorus, high-potassium 3-18-18 starter for new seed and sod, where it drives root establishment. On an established lawn, use it for fall hardiness only when a soil test shows phosphorus or potassium is needed.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41oLu+gvyrL'),
      badge: { label: 'Best Starter', tone: 'purple' },
    },
  ],
  'how-to-apply-liquid-lawn-fertilizer': [
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'The balanced all-purpose liquid used as the example here: 16-4-8 with a hose-end sprayer, easy to apply evenly on any grass type.',
      insertAfterH2: 3,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best balanced liquid feed', tone: 'gold', tier: 'editor' },
    },
  ],
  // === Best Weed and Feed for Centipede Grass (2026) ===
  // Centipede is sensitive: most weed-and-feed harms it, so picks lead with the few
  // centipede-safe options. All ASINs PAAPI-verified in-stock 2026-06-26. Scotts Bonus S
  // (B00GPAGO6S) and EdgeGuard Mini (B002YPS1KK) were OUT OF STOCK, so DLX is used instead.
  // One product display per section: card after H2 1/3/4/5, decision table lives in H2 2.
  'best-weed-and-feed-for-centipede-grass-2026': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Before you buy any product, test your soil. Centipede health hinges on acidic pH (5.0 to 6.0) and low phosphorus, and this kit reports both with tailored recommendations.',
      insertAfterH2: 1,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'orange' },
    },
    {
      name: 'Fertilome Centipede Weed & Feed 15-0-15 (16 lbs)',
      affiliateUrl: amazonUrl('B0CYJMR85M'),
      price: '42.99',
      description: 'The rare combo built for centipede biology: a 15-0-15 ratio with zero phosphorus, iron for color, and an atrazine herbicide that controls 200+ broadleaf weeds without scorching the turf.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51WYEpDs6SL'),
      badge: { label: 'Best for Centipede', tone: 'green' },
    },
    {
      name: 'Southern Ag Atrazine Weed Killer for St. Augustine & Centipede (32 oz)',
      affiliateUrl: amazonUrl('B0052MZBYQ'),
      price: '16.49',
      description: 'The centipede-safe standalone herbicide for the smarter feed-and-weed-separately approach. One quart treats about 3,700 sq ft and works as a pre-emergent and post-emergent on broadleaf weeds.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41xJ0HypHcL'),
      badge: { label: 'Best Standalone Herbicide', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard DLX Broadcast Spreader',
      affiliateUrl: amazonUrl('B001H1EQO2'),
      price: '94.96',
      description: 'Even, calibrated coverage is what keeps granular weed and feed from burning centipede in spots. The EdgeGuard side-shield keeps product off beds and walkways.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31ksUhTEIUL'),
      badge: { label: 'Spreads It Evenly', tone: 'slate' },
    },
  ],
  // === 2026-06-24 proven-winner surfacing sweep (Amazon shipped-revenue + PostHog) ===
  // Added curated picks for high-traffic fertilizer posts that had NO hand-authored
  // affiliate-block (so the curated path, which does not auto-skip that guard, won't
  // double-display). All ASINs PAAPI-verified in-stock 2026-06-24. Milorganite 32 lb
  // (B0DDHTJKCY) was OUT OF STOCK at verification, so it is intentionally excluded.
  // 2026-08-18 audit: the lead slot held the SLS liquid while the H2 below it
  // named Milorganite as the pick (self-contradiction), and Pennington 32-0-5
  // converted 0.49% here (1.3% site-wide — killed everywhere). Milorganite takes
  // the lead, PGF moves to the comparison-table section, the liquid re-slots to
  // the section whose copy argues for it, Scotts Southern replaces Pennington.
  // ASINs/prices/images reused from verified sibling entries.
  'best-summer-lawn-fertilizers-keep-grass-green-healthy': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The summer pick this guide actually names: slow-release organic nitrogen with iron that will not burn the lawn in heat, feeding steadily for 8 to 10 weeks.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'Best Summer Fertilizer', tone: 'gold', tier: 'editor' },
      shortName: 'Milorganite',
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The all-around summer pick: a balanced 16-4-8 with 7% humic DG and super-fine particles that spread evenly and feed steadily without surging top growth in the heat.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best All-Around Granular', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A balanced 16-4-8 liquid you spray through any hose-end sprayer for fast, even summer green-up. Gentle enough to use in the heat without the burn risk of heavy granular nitrogen.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid for Fast Green-Up', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'For warm-season lawns pushing through peak heat: a Southern-formulated feed built to hold color through summer stress across a 15,000 sq ft bag.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'Best for Warm-Season Heat', tone: 'orange' },
      shortName: 'Scotts Southern',
    },
  ],
  'best-fertilizer-for-st-augustine-grass-in-texas': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 that suits Texas St. Augustine well: steady nitrogen for color, light phosphorus and potassium for roots, plus humic DG for the sandy and alkaline soils common across the state.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced Granular', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick green-up between granular feedings, easy to apply evenly on St. Augustine through a hose-end sprayer during the long Texas growing season.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'blue' },
    },
  ],

  // Regional reuse of the verified Texas St. Augustine fertilizer ASINs, surfaced early.
  // 2026-07-11 affiliate de-risk: hero rotated off the PGF monoculture to the
  // Southern-formulated Scotts granular (the content block already recommends it,
  // B000UGU59K PAAPI-verified in-stock). PGF kept as a strong secondary so every
  // state page carries two independent granular revenue streams. disableLeadAnchor
  // keeps every card at its section boundary (the intro already has an affiliate block).
  'best-fertilizer-for-st-augustine-grass-in-florida': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Formulated for Southern grasses like St. Augustine, with slow-release nitrogen that greens Florida lawns without surging growth in the heat and sandy soils.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 that fits Florida St. Augustine: steady nitrogen for color, light phosphorus and potassium for roots, plus humic DG for the sandy soils common across the state.',
      insertAfterH2: 3,
      shortName: 'Andersons PGF Complete',
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick green-up between granular feedings, easy to apply evenly on St. Augustine through a hose-end sprayer during Florida\'s long growing season.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'green' },
    },
  ],

  // 2026-07-10 St. Augustine-by-state cluster launch (GA/SC/LA + Bermuda AL).
  // Same regional-reuse pattern as the Florida entry above: verified ASINs from
  // the Texas St. Augustine set. Each generated post already carries an
  // in-content affiliate block in its FIRST H2 section, so disableLeadAnchor
  // keeps every pick at its configured later-section boundary (Codex P1,
  // 2026-07-10: the lead pick would otherwise snap to the near-top anchor and
  // stack a second product display next to that block).
  'best-fertilizer-for-st-augustine-grass-in-georgia': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Built for Southern grasses like St. Augustine, feeding steadily across both Georgia Piedmont clay and Coastal Plain sand without a growth surge.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 that suits Georgia St. Augustine: steady nitrogen for color, light phosphorus and potassium for roots, plus humic DG that helps on both Piedmont clay and Coastal Plain sand.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      shortName: 'Andersons PGF Complete',
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick green-up between granular feedings, easy to apply evenly on St. Augustine through a hose-end sprayer across Georgia\'s long warm season.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'green' },
    },
  ],
  'best-fertilizer-for-st-augustine-grass-in-south-carolina': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Made for Southern grasses like St. Augustine, with slow-release feeding that suits sandy Lowcountry soils and holds color through a South Carolina summer.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 for South Carolina St. Augustine: steady nitrogen for color with humic DG that helps sandy Lowcountry soils hold nutrients instead of leaching them.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      shortName: 'Andersons PGF Complete',
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick correction of coastal yellowing between granular feedings, applied evenly through a hose-end sprayer.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'green' },
    },
  ],
  'best-fertilizer-for-st-augustine-grass-in-louisiana': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Designed for Southern grasses like St. Augustine, with slow-release nitrogen that holds up to heavy Gulf Coast rain better than quick-release blends.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 for Louisiana St. Augustine: steady slow-release nitrogen that holds up under Gulf Coast rainfall better than quick-release blends, plus humic DG for sandy and alluvial soils.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      shortName: 'Andersons PGF Complete',
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick green-up after heavy Louisiana rain leaches nutrients, easy to meter through a hose-end sprayer between granular feedings.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'green' },
    },
  ],
  'best-fertilizer-for-bermuda-grass-in-alabama': [
    {
      name: 'Pennington Full Season Lawn Fertilizer 32-0-5',
      affiliateUrl: amazonUrl('B0DYWRC5NM'),
      price: '27.99',
      description: 'A high-nitrogen 32-0-5 with iron built for Alabama Bermuda pushing through summer heat: the exact NPK shape this guide recommends, covering 5,000 sq ft per bag.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('51wvj2XoEQL'),
      badge: { label: 'Best for Alabama Summers', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for spoon-feeding Bermuda between granular applications, the golf-course technique the guide describes, applied through any hose-end sprayer.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Feed', tone: 'blue' },
    },
  ],

  // === 2026-07-11 geo cluster launch (centipede + bermuda + zoysia + KBG states) ===
  // Diversified heroes by grass agronomy (NOT PGF-by-default): centipede -> low-N
  // Milorganite (PGF's 16-4-8 over-feeds centipede); bermuda -> high-N Pennington;
  // zoysia -> Scotts Southern granular; KBG (cool-season) -> balanced PGF. Every
  // GENERATED DRAFT below carries an in-content affiliate block in its first section,
  // so disableLeadAnchor keeps each card at its configured H2 boundary. EXCEPTION: the
  // pre-existing published centipede GA/SC entries have NO in-content block and
  // intentionally OMIT disableLeadAnchor (see their own comment). All ASINs
  // PAAPI-verified in-stock 2026-07-11.
  'best-fertilizer-for-centipede-grass-in-florida': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'A low-nitrogen 6-4-0 that suits centipede\'s light appetite, feeding slowly so Florida lawns green up without the surge that makes centipede decline.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('51+RWV7fs3L'),
      shortName: 'Milorganite',
      badge: { label: 'Best for Centipede', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'A phosphorus-free 15-0-15 that adds potassium for stress tolerance without the phosphorus most Florida soils already carry in excess.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41wW+L4asjL'),
      shortName: 'Simple Lawn Solutions 15-0-15',
      badge: { label: 'Best Phosphorus-Free', tone: 'green' },
    },
  ],
  'best-fertilizer-for-centipede-grass-in-alabama': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'A low-nitrogen 6-4-0 that matches centipede\'s slow-feeding needs, greening Alabama lawns gently without pushing the growth that thins centipede out.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('51+RWV7fs3L'),
      shortName: 'Milorganite',
      badge: { label: 'Best for Centipede', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'A phosphorus-free 15-0-15 for potassium and summer hardiness, an easy hose-end feed between the main slow-release applications.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41wW+L4asjL'),
      shortName: 'Simple Lawn Solutions 15-0-15',
      badge: { label: 'Best Phosphorus-Free', tone: 'green' },
    },
  ],
  // Centipede GA/SC pre-existed (published, ids 402/411) from an earlier run and were
  // outside the 12-draft batch. These two have NO in-content affiliate block (unlike the
  // generated FL/AL drafts), so they OMIT disableLeadAnchor: the low-N Milorganite hero
  // lead-anchors to the top the way a commercial-intent post should. SC (411) had zero
  // affiliate links before this. Same low-N centipede agronomy as FL/AL: no PGF.
  'best-fertilizer-for-centipede-grass-in-georgia': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'A low-nitrogen 6-4-0 that fits centipede\'s light appetite, feeding Georgia lawns slowly so they green up without the surge that thins centipede out.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51+RWV7fs3L'),
      shortName: 'Milorganite',
      badge: { label: 'Best for Centipede', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'A phosphorus-free 15-0-15 that adds potassium for summer hardiness without the phosphorus Georgia clay and Coastal Plain soils often already hold.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41wW+L4asjL'),
      shortName: 'Simple Lawn Solutions 15-0-15',
      badge: { label: 'Best Phosphorus-Free', tone: 'green' },
    },
  ],
  'best-fertilizer-for-centipede-grass-in-south-carolina': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'A low-nitrogen 6-4-0 matched to centipede\'s slow-feeding needs, greening South Carolina lawns gently without pushing growth centipede cannot sustain.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51+RWV7fs3L'),
      shortName: 'Milorganite',
      badge: { label: 'Best for Centipede', tone: 'gold' },
    },
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'A phosphorus-free 15-0-15 for potassium and stress tolerance on sandy Lowcountry soils, an easy hose-end feed between slow-release applications.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41wW+L4asjL'),
      shortName: 'Simple Lawn Solutions 15-0-15',
      badge: { label: 'Best Phosphorus-Free', tone: 'green' },
    },
  ],
  'best-fertilizer-for-bermuda-grass-in-south-carolina': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for the stretches bermuda wants steadier feeding, spreading evenly across coastal sand and Piedmont clay alike.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-bermuda-grass-in-tennessee': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for steady mid-season feeding, an even-spreading granular that holds color without a growth surge.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-bermuda-grass-in-oklahoma': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG that helps Oklahoma\'s clay soils hold nutrients between the deep, infrequent waterings bermuda prefers.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-bermuda-grass-in-mississippi': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for steady feeding through the Deep South summer, spreading evenly for consistent color.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-zoysia-grass-in-georgia': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Formulated for Southern grasses like zoysia, feeding steadily across Georgia clay and sand without pushing the growth zoysia is slow to use.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for the main feedings, an even-spreading granular that suits zoysia\'s moderate appetite.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-zoysia-grass-in-texas': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Built for Southern grasses like zoysia, with slow-release feeding that holds color through a long Texas season without over-pushing growth.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG that helps Texas clay and alkaline soils feed zoysia evenly between applications.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-zoysia-grass-in-north-carolina': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'Made for Southern grasses like zoysia, feeding gently through North Carolina\'s transition-zone season so the lawn greens without a surge.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41k2rKURWKL'),
      shortName: 'Scotts Southern',
      badge: { label: 'Best for Southern Lawns', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for steady mid-season feeding, spreading evenly across Piedmont clay and coastal sand.',
      insertAfterH2: 3,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced 16-4-8', tone: 'blue' },
    },
  ],
  'best-fertilizer-for-kentucky-bluegrass-in-colorado': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG that gives Colorado Kentucky bluegrass steady nitrogen for color at altitude without forcing thirsty growth.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced Granular', tone: 'gold' },
    },
  ],
  'best-fertilizer-for-kentucky-bluegrass-in-ohio': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG for Ohio Kentucky bluegrass, metering nitrogen over weeks for even color through the cool-season growth flushes.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced Granular', tone: 'gold' },
    },
  ],
  'best-fertilizer-for-kentucky-bluegrass-in-pennsylvania': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG that carries Pennsylvania Kentucky bluegrass through spring and fall feeding without a growth surge.',
      insertAfterH2: 2,
      disableLeadAnchor: true,
      imageUrl: amazonImg('41xelE+7eJL'),
      shortName: 'Andersons PGF Complete',
      badge: { label: 'Best Balanced Granular', tone: 'gold' },
    },
  ],
  'best-fertilizer-ratio-bermuda-grass-npk-guide': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A textbook 16-4-8 ratio in a single bag, the same 4-1-2 balance this guide recommends for Bermuda. Super-fine particles spread evenly so the ratio actually lands where you want it.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41xelE+7eJL'),
      // 2026-07-01 retier: promoted to verdict tier (stars + verdict-card placement).
      badge: { label: 'Balanced 16-4-8 Pick', tone: 'gold', rating: 4.7, tier: 'verdict' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'The same balanced 16-4-8 ratio in liquid form, handy when you want to dial in the numbers between granular feedings or spoon-feed Bermuda through summer.',
      insertAfterH2: 3,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Liquid 16-4-8 Option', tone: 'blue' },
    },
  ],
  // 92 per-product impressions and 0 clicks over 14d (2026-09-13 affiliate
  // refresh) with a single feeding card snapped to the near-top lead anchor —
  // the one section where this article argues AGAINST reaching for a bag
  // ("the right approach starts with diagnosis, not a bag of fertilizer", and
  // hold nitrogen until new green shoots). `disableLeadAnchor` keeps both picks
  // at their configured boundaries so each lands in the step whose copy asks for
  // it: iron in the stabilize step, the balanced feed in the regrowth step.
  // Slot 4 is the reminder widget (data/blog-reminder-widgets), hence 3 and 5.
  'revive-bermuda-grass-after-summer-heat-stress': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'When you are ready to resume feeding recovering Bermuda, a gentle balanced 16-4-8 restarts growth without the surge of a high-nitrogen quick-release. Humic DG also helps a stressed root zone bounce back.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Balanced Recovery Feed', tone: 'green', tier: 'editor' },
      shortName: 'Andersons PGF Complete',
      disableLeadAnchor: true,
    },
    {
      name: 'Simple Lawn Solutions Liquid Iron Darker Green (32 oz)',
      affiliateUrl: amazonUrl('B08Z4DTTJR'),
      price: '23.99',
      description: 'Chelated liquid iron for color on stressed Bermuda without a growth push. It darkens the blades the lawn still has while you keep nitrogen off, which is the whole point of the stabilizing step.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51OgZKkTOPL'),
      badge: { label: 'Color Without Nitrogen', tone: 'blue', tier: 'pick' },
      shortName: 'Liquid Iron',
      disableLeadAnchor: true,
    },
  ],
  // === 2026-06-19 competitor-conquest series: The Lawn Care Nut / Yard Mastery ===
  // Post #1 of the conquest set. Honest cost-per-lb-N comparison. These curated
  // picks render the closing "pick by goal" block + the on-intent sidebar (first 3),
  // and suppress the off-intent category fallback. ASINs PAAPI-verified in-stock
  // 2026-06-19. Milorganite stays on the 5 lb (B000UOB1P4) HERE and in
  // bio-nite-vs-milorganite: both posts build a size-matched cost-per-lb-N /
  // comparison table around the 5 lb bag (390 sq ft, $16.55, $55.17/lb N), so the
  // 32 lb would break the numbers. The 5 lb is in stock. Every OTHER Milorganite
  // surface moved to the 32 lb (B0FGDNF55M) on 2026-07-06.
  'is-yard-mastery-flagship-24-0-6-worth-it': [
    {
      name: 'Yard Mastery Flagship 24-0-6 Granular Lawn Fertilizer (45 lb)',
      affiliateUrl: amazonUrl('B08YGTH3P2'),
      price: '79.94',
      description: 'The Lawn Care Nut bag itself, and the value pick on this list at about $7.40 per pound of nitrogen. A high-N 24-0-6 with 3% iron and slow-release Bio-Nite. Get the 45 lb bag, not the small one.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41m5V6GZRBL'),
      badge: { label: 'Best Value Per lb N', tone: 'gold' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'Costs more per pound of nitrogen, but the dispersing granule lays down far more evenly and adds 7% humic acid. Choose it when coverage quality and soil health matter more than nitrogen cost.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best for Even Coverage', tone: 'blue' },
    },
    {
      // Kept on the 5 lb (B000UOB1P4): the post's cost-per-lb-N table is built on
      // the 5 lb bag (390 sq ft, $16.55, $55.17/lb N). In stock, so no dead link.
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen (5 lb)',
      affiliateUrl: amazonUrl('B000UOB1P4'),
      price: '16.63',
      description: 'The forgiving, almost impossible to burn organic option. Not your cheap nitrogen source, but great for slow, steady feeding and building soil.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51ktZqjDYvL'),
      badge: { label: 'Best Organic Slow-Release', tone: 'green' },
    },
  ],
  // === 2026-06-19 competitor-conquest series (posts 2-5): Lawn Care Nut / Yard Mastery ===
  // Honest comparison/review posts. Curated picks render the closing block + the on-intent
  // sidebar and suppress the off-intent category fallback. ASINs + image IDs PAAPI-verified
  // in-stock 2026-06-19; each post was Codex-adversarially reviewed before creation.
  "bio-nite-vs-milorganite": [
    {
      // Kept on the 5 lb (B000UOB1P4): this comparison post's table pits it against
      // Bio-Turf (4 lb) as a like-sized bag, and the body explicitly contrasts the
      // "5 lb" against "the big-box 32-pounders". The 5 lb is in stock, so no dead
      // link here. Every other Milorganite surface moved to the 32 lb (B0FGDNF55M).
      name: "Milorganite 6-4-0 Slow-Release Nitrogen (5 lb)",
      affiliateUrl: amazonUrl("B000UOB1P4"),
      price: "16.63",
      description: "The original heat-dried biosolid and the most forgiving organic feed here. Almost impossible to burn, adds phosphorus and ~2.5% iron, and builds soil over time.",
      insertAfterH2: 1,
      imageUrl: amazonImg("51ktZqjDYvL"),
      badge: { label: "Most Forgiving Feed", tone: "blue" },
    },
    {
      name: "Down to Earth Bio-Turf 8-3-5 (4 lb)",
      affiliateUrl: amazonUrl("B0BRKJJ89Z"),
      price: "22.49",
      description: "A poultry-litter and plant-based organic blend, not a sewage biosolid, but it lands in a similar slow-release place with a higher nitrogen number and added potassium. Choose it if you want more N and some K from a non-sewage organic source.",
      insertAfterH2: 6,
      imageUrl: amazonImg("51eO4xr-2jL"),
      badge: { label: "Higher N, Non-Sewage Organic", tone: "green" },
    },
    {
      name: "Yard Mastery Flagship 24-0-6 Granular Lawn Fertilizer (45 lb, with Bio-Nite)",
      affiliateUrl: amazonUrl("B08YGTH3P2"),
      price: "79.94",
      description: "The only way to buy Bio-Nite is inside a Yard Mastery bag. This Flagship blend pairs the Bio-Nite biosolid carrier with a lot more nitrogen, 3% iron, and potassium for a 24-0-6 high-N feed, so it is a different product than straight Milorganite, not a pure organic.",
      insertAfterH2: 2,
      imageUrl: amazonImg("41m5V6GZRBL"),
      badge: { label: "The Only Way to Buy Bio-Nite", tone: "orange" },
    },
  ],
  "is-yard-mastery-bio-stimulant-program-worth-it": [
    {
      name: "GS Plant Foods Liquid Humic Acid (1 gal, OMRI listed)",
      affiliateUrl: amazonUrl("B00CSAOFUG"),
      price: "41.95",
      description: "The honest stand-in for RGS, Humic12, and the Air-8 bottle, since all three lean on humic acid. A straight, OMRI-listed leonardite-derived concentrate that improves soil structure and nutrient holding over a couple of seasons. Buy this instead of three branded bottles.",
      insertAfterH2: 1,
      imageUrl: amazonImg("41+7DyfJKtL"),
      badge: { label: "Best Soil-Building Value", tone: "gold" },
    },
    {
      name: "GS Plant Foods Liquid Kelp Fertilizer (1 gal, OMRI listed)",
      affiliateUrl: amazonUrl("B089HSJQ4Q"),
      price: "39.95",
      description: "The buyable analog for MicroGreene. An OMRI-listed seaweed concentrate that supports color and stress tolerance, and one gallon makes 800-plus gallons of spray. Pick this for green-up without forcing nitrogen growth.",
      insertAfterH2: 3,
      imageUrl: amazonImg("41U6dNBmmjL"),
      badge: { label: "Best for Color and Stress", tone: "green" },
    },
  ],
  "when-to-use-high-potassium-summer-fertilizer": [
    {
      name: "Yard Mastery Stress Blend 7-0-20 (45 lb)",
      affiliateUrl: amazonUrl("B08YS4CTRM"),
      price: "79.99",
      description: "The named example throughout this guide. A high-potassium, low-nitrogen 7-0-20 with 3% iron and slow-release Bio-Nite. Covers 15,000 sq ft, so it is the right size for most full-size lawns. Reach for this when the lawn is under heat, drought, or wear stress and you want color without pushing growth.",
      insertAfterH2: 2,
      imageUrl: amazonImg("413lO9oo7XL"),
      badge: { label: "Best High-K Summer Blend", tone: "gold" },
    },
    {
      name: "Yard Mastery Stress Blend 7-0-20 (18 lb)",
      affiliateUrl: amazonUrl("B09X5YXD7D"),
      price: "49.99",
      description: "Same 7-0-20 formula in a smaller bag that covers 6,000 sq ft. The better-value pick if your lawn is small and the 45 lb bag would sit half-used in the garage for a year. Otherwise the bigger bag is cheaper per pound.",
      insertAfterH2: 3,
      imageUrl: amazonImg("41MZ7Qe7UbL"),
      badge: { label: "Best for Smaller Lawns", tone: "green" },
    },
    {
      name: "Scotts Turf Builder EdgeGuard Mini Broadcast Spreader",
      affiliateUrl: amazonUrl("B002YPS1KK"),
      price: "52.99",
      description: "A granular blend only works if it goes down evenly, and that needs a calibrated spreader. The EdgeGuard Mini holds up to 15,000 sq ft of product and shuts off the spread pattern at edges to keep granules off beds and pavement.",
      insertAfterH2: 4,
      imageUrl: amazonImg("31WEgJaq8FL"),
      badge: { label: "Spreader You Will Need", tone: "slate" },
    },
  ],
  "is-the-yard-mastery-spreader-worth-it": [
    {
      name: "Scotts Turf Builder EdgeGuard Mini Broadcast Spreader",
      affiliateUrl: amazonUrl("B002YPS1KK"),
      price: "52.99",
      description: "The no-fuss pick for most homeowners. Holds enough product for up to 15,000 sq ft, and the EdgeGuard side-blocker keeps fertilizer out of your beds and the street. Does everything a small to mid-size lawn needs for under fifty bucks.",
      insertAfterH2: 1,
      imageUrl: amazonImg("31WEgJaq8FL"),
      badge: { label: "Best Value", tone: "gold" },
    },
    {
      name: "Scotts Elite Spreader",
      affiliateUrl: amazonUrl("B077JBGG4H"),
      price: "169.99",
      description: "The step up when you want pro-level even coverage and a bigger hopper for larger lawns. Dual rotors lay product down more uniformly, and it still costs well under a branded commercial unit.",
      insertAfterH2: 3,
      imageUrl: amazonImg("317Nz1K222L"),
      badge: { label: "Step-Up Accuracy", tone: "blue" },
    },
  ],
  // === 2026-06-19 affiliate audit: branded-guide product-fit picks ===
  // These branded product-usage guides previously rendered only the generic
  // CategoryProductCallout fallback, which surfaced off-intent products (e.g. the
  // Spectracide guide showed Scotts/Tenacity; the Triple Action guide showed a push
  // reel mower). Curated picks below show the actual products the searcher came for
  // and suppress the off-intent fallback. ASINs PAAPI-verified in-stock 2026-06-19.
  'spectracide-weed-stop-instructions-use-best-results': [
    {
      name: 'Spectracide Weed Stop For Lawns Concentrate (32 oz)',
      affiliateUrl: amazonUrl('B07K78HDDS'),
      price: '7.47',
      description: 'The standard pick for dandelions, clover, and chickweed. Mix this 32 oz concentrate in a pump or hose-end sprayer to treat broadleaf weeds across most common lawn grasses without harming the turf.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41oLm58LEiL'),
      badge: { label: 'Best for Broadleaf Weeds', tone: 'green', tier: 'editor' },
    },
    {
      // 2026-08-18 CTR pass: the lead pick is a concentrate the reader already owns,
      // but the page showed nothing to mix and spray it with. Sprayer ASIN/price/image
      // reused from the PAAPI-verified Tenacity guide entry.
      name: 'Ortho Dial N Spray Hose-End Sprayer (14 Dilution Settings)',
      affiliateUrl: amazonUrl('B0071D0EZK'),
      price: '15.97',
      description: 'Weed Stop concentrate has to be mixed and sprayed. A metered hose-end sprayer dials in the label rate and covers the lawn evenly without a separate tank.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41H+XAMwUxS'),
      badge: { label: 'To Mix and Spray It', tone: 'slate' },
    },
    {
      name: 'Spectracide Weed Stop For Lawns Plus Crabgrass Killer Concentrate (32 oz)',
      affiliateUrl: amazonUrl('B00OINHBUW'),
      price: '8.48',
      description: 'Choose this version when crabgrass or other grassy weeds are in the mix. It adds quinclorac to the broadleaf formula, so one bottle handles the dandelions and the young crabgrass together.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41k7wMmV24L'),
      badge: { label: 'Adds Crabgrass Control', tone: 'blue' },
    },
    {
      name: 'Spectracide Weed Stop Plus Crabgrass Killer, AccuShot Sprayer (1.33 gal)',
      affiliateUrl: amazonUrl('B07K788W1C'),
      price: '21.98',
      description: 'No mixing and no measuring. The battery-powered AccuShot sprayer arrives ready to use and covers a large lawn fast, with the crabgrass-plus-broadleaf formula built in.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51AOdZpuVKL'),
      badge: { label: 'No-Mix, Ready to Use', tone: 'orange' },
    },
  ],

  'scotts-turf-builder-triple-action-instructions-complete-application-guide': [
    {
      name: 'Scotts Turf Builder Triple Action',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: 'The product this guide covers. One spring application feeds the lawn, kills listed broadleaf weeds, and prevents crabgrass for up to 4 months. Apply when soil temps hold near 55F.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Best 3-in-1', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Triple Action is a granular product, so you need a calibrated spreader to apply it evenly. The EdgeGuard Mini blocks the spread pattern at edges to keep granules off beds and pavement.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
    {
      name: 'Tenacity Turf Herbicide (8 oz)',
      affiliateUrl: amazonUrl('B005DUTNF0'),
      price: '62.06',
      description: 'For the tough broadleaf and grassy weeds Triple Action does not fully control, spot-treat with mesotrione. A little goes a long way and it is safe over most listed lawn grasses.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41tEE7n3sRL'),
      badge: { label: 'For Weeds It Misses', tone: 'green' },
    },
  ],

  // === 2026-06-19 affiliate audit (rendered): application-gear + product-fit picks ===
  // The rendered-page audit found these high-traffic fertilizer/weed guides either rendered
  // an off-intent category fallback (the blog template feeds the sidebar from
  // getSidebarProductsForCategory when a slug has no picks) or linked no broadcast spreader /
  // soil-test / sprayer despite the topic requiring them. A curated entry overrides BOTH the
  // in-content callout and the sidebar (which uses the first 3 picks) and adds the high-AOV
  // gear. ASINs PAAPI-verified in-stock 2026-06-19.
  'often-you-apply-scotts-weed-feed': [
    {
      // 2026-07-15 CTR pass: high-traffic timing post carried only gear/soil-test support
      // cards, never the product readers are scheduling. Same verified ASIN as the
      // buyers-guide and application-guide entries.
      name: 'Scotts Turf Builder Weed and Feed 3',
      affiliateUrl: amazonUrl('B00XJM8KMQ'),
      price: '64.99',
      description: 'The weed and feed this schedule applies to. One bag covers the spring pass, and the 6-to-8-week minimum between applications in this guide comes straight from its label.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41IVt6FtH2L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts Weed & Feed 3',
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Scotts weed and feed is granular, so it needs a calibrated broadcast spreader to go down evenly. The EdgeGuard Mini shuts off the spread pattern at edges to keep product off beds and pavement.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'A mail-in lab test tells you whether your lawn actually needs the nitrogen in weed and feed, so you feed on a schedule that fits your soil instead of guessing.',
      insertAfterH2: 5,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
  ],

  'long-scotts-weed-feed-take-work': [
    {
      name: 'Scotts Turf Builder Weed and Feed 3',
      affiliateUrl: amazonUrl('B00XJM8KMQ'),
      price: '64.99',
      description: 'The granular flagship this timeline covers. Expect visible weed curling in 5 to 10 days and full dieback in 2 to 3 weeks when applied to a dewy lawn at 55F+ soil temps.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41IVt6FtH2L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts Weed & Feed 3',
    },
    {
      name: 'Scotts Turf Builder Triple Action',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: 'Works on a similar clock but adds a crabgrass pre-emergent layer, so one early-spring pass covers weeds, crabgrass, and feeding while you wait out the same 2 to 3 week dieback window.',
      insertAfterH2: 6,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Faster Spring Combo', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Uneven spreading is the top reason weed and feed "takes forever" in patches. A calibrated broadcast spreader puts down the labeled rate evenly so the whole lawn hits the same timeline.',
      insertAfterH2: 9,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'best-fertilizer-for-zoysia-grass-2026-guide': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The all-around zoysia pick: a balanced 16-4-8 with humic DG and super-fine particles that feed steadily without pushing the surge growth zoysia hates to recover from.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Overall for Zoysia', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick color between granular feedings, sprayed through the attached hose-end sprayer. Easy to keep light, which suits zoysia\'s modest appetite.',
      insertAfterH2: 3,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Option', tone: 'blue' },
    },
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'A low-nitrogen 6-4-0 that matches zoysia\'s lower nitrogen appetite covered in this section, feeding slowly with no burn risk even if you overlap passes.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'Low-Nitrogen Match', tone: 'green' },
    },
  ],

  'best-fertilizer-kentucky-bluegrass-2026-guide': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with humic DG that feeds Kentucky bluegrass steadily through the cool-season growth windows, with fine particles that spread evenly on dense turf.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Overall for KBG', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder Lawn Food',
      affiliateUrl: amazonUrl('B005W1QXQW'),
      price: '21.79',
      description: 'The budget granular staple for cool-season lawns: all-grass-type feeding that works well for the spring and early-fall KBG applications in this schedule.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41-eKeqethL'),
      badge: { label: 'Budget Pick', tone: 'slate' },
    },
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The slow-release organic option for the summer window, when Kentucky bluegrass is heat-stressed and fast nitrogen does more harm than good. No burn risk.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'Safest Summer Feed', tone: 'green' },
    },
  ],

  'best-fertilizers-for-a-green-lawn-this-july': [
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A balanced 16-4-8 liquid you spray through any hose-end sprayer for fast, even July green-up. Gentle enough to use in the heat without the burn risk of heavy granular nitrogen.',
      insertAfterH2: 2,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best for July Heat', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The granular route for July: super-fine particles with humic DG that feed steadily without surging top growth while the lawn is fighting heat.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Granular', tone: 'blue' },
    },
  ],

  'benefits-of-using-liquid-kelp-on-your-lawn': [
    {
      name: 'GS Plant Foods Liquid Kelp Fertilizer (1 gal, OMRI listed)',
      affiliateUrl: amazonUrl('B089HSJQ4Q'),
      price: '39.95',
      description: 'The OMRI-listed seaweed concentrate this guide is about. One gallon makes 800-plus gallons of spray, supporting color and stress tolerance without forcing nitrogen growth.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41U6dNBmmjL'),
      badge: { label: 'The Kelp to Buy', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'Kelp is a supplement, not a meal. This 16-4-8 liquid with seaweed and humic acid already in the jug is the simplest way to pair kelp benefits with the NPK your lawn still needs.',
      insertAfterH2: 7,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Pair With Real NPK', tone: 'blue' },
    },
  ],

  'scotts-weed-feed-pre-emergent': [
    {
      name: 'Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.',
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: 'The direct answer to this question: Scotts\' actual pre-emergent. It stops crabgrass and listed grassy weeds before they germinate, which standard weed and feed cannot do.',
      insertAfterH2: 2,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: 'The Actual Pre-Emergent', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder Triple Action',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: 'The one Scotts bag that combines pre-emergent, broadleaf weed killer, and fertilizer: the closest thing to a weed and feed with pre-emergent built in.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Best 3-in-1 Alternative', tone: 'blue' },
    },
  ],

  'bermuda-grass-spring-green-up-starts-speed-up': [
    {
      name: 'REOTEMP Waterproof Soil Thermometer, 5" Stem',
      affiliateUrl: amazonUrl('B07F93K8ZY'),
      price: '12.99',
      description: 'Bermuda greens up when soil holds 65F at 4 inches, not when the air feels warm. A stem thermometer tells you the real date so you feed at green-up, not before it.',
      insertAfterH2: 2,
      imageUrl: amazonImg('21ZMzIf-j4L'),
      badge: { label: 'Know Your Green-Up Date', tone: 'blue' },
    },
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'Once soil temps hold 65F, this balanced 16-4-8 with humic DG is the first feeding that pushes bermuda from patchy wake-up to full green without surge growth.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'First Spring Feeding', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Pennington Ironite All-Purpose 1-0-1 Lawn & Garden Fertilizer',
      affiliateUrl: amazonUrl('B07FSV88HS'),
      price: '42.20',
      description: 'For bermuda that is awake but pale: iron deepens color fast without adding nitrogen, useful when a cold snap or shade is holding your green-up back.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51qEgPQgN0L'),
      badge: { label: 'Fast Color Boost', tone: 'green' },
    },
  ],

  'best-fertilizers-for-lawns': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The safest single answer to the question this post asks: a balanced 16-4-8 with humic DG and super-fine particles that feed any grass type steadily without surge growth.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Overall Fertilizer', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The slow-release organic option on the label checklist: 6-4-0 with no burn risk, a forgiving pick if this is your first season fertilizing.',
      insertAfterH2: 6,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'Easiest to Apply Safely', tone: 'green' },
    },
    {
      name: 'Pennington Full Season Lawn Fertilizer 32-0-5',
      affiliateUrl: amazonUrl('B0DYWRC5NM'),
      price: '27.99',
      description: 'A high-nitrogen 32-0-5 with iron and controlled-release coverage for the season, the budget route to the high-nitrogen slow-feed combo this section describes.',
      insertAfterH2: 8,
      imageUrl: amazonImg('51wvj2XoEQL'),
      badge: { label: 'Best High-Nitrogen Value', tone: 'blue' },
    },
  ],

  'how-to-improve-soil-ph-for-grass': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'The mail-in lab test this step is about: an exact pH reading plus nutrient levels, so you know whether you need lime, sulfur, or neither before buying either.',
      insertAfterH2: 2,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test Before You Amend', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Encap Fast Acting Pelletized Lime (25 lb)',
      affiliateUrl: amazonUrl('B0924ZZT5C'),
      price: '36.99',
      description: 'Pelletized lime that spreads through a regular broadcast spreader and starts moving acidic soil toward neutral faster than ag lime dust.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51gvJl9gbfL'),
      badge: { label: 'For Raising pH', tone: 'blue' },
    },
  ],

  'how-to-calculate-fertilizer-for-lawn': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A worked example for the formula: 16% nitrogen means 6.25 lbs of product per 1,000 sq ft to hit the standard 1 lb of nitrogen rate.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Easy-Math 16-4-8', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'The calculation only holds if the spreader puts it down at the rate you solved for. A calibrated broadcast spreader with EdgeGuard keeps the math honest at the edges.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Calibration Matters', tone: 'slate' },
    },
  ],

  'scotts-southern-turf-builder-instructions': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'The bag this guide covers: slow-release nitrogen formulated for heat-tolerant Southern grasses like St. Augustine, bermuda, and centipede, feeding steadily without surge growth.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Southern Turf Builder is granular, so the step-by-step rates here assume a calibrated broadcast spreader. The EdgeGuard shuts off the pattern at edges to keep product off beds and pavement.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'scotts-weed-feed-free-printable-application-guide-instructio': [
    {
      name: 'Scotts Turf Builder Weed and Feed 3',
      affiliateUrl: amazonUrl('B00XJM8KMQ'),
      price: '64.99',
      description: 'The flagship product this printable guide walks through: a 2,4-D blend that kills dandelions and clover while feeding the lawn, spread dry on a dewy morning at 55F+ soil temps.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41IVt6FtH2L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder Triple Action',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: 'If crabgrass is on your checklist too, Triple Action follows the same application steps but adds a pre-emergent layer, so one spring pass covers weeds, crabgrass, and feeding.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Best 3-in-1 Upgrade', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Every rate on the printable chart assumes a calibrated broadcast spreader. The EdgeGuard Mini matches the Scotts spreader settings printed on the bag exactly.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Matches the Bag Settings', tone: 'slate' },
    },
  ],

  'scotts-turf-builder-lawn-food-when-how-to-apply': [
    {
      name: 'Scotts Turf Builder Lawn Food',
      affiliateUrl: amazonUrl('B005W1QXQW'),
      price: '21.79',
      description: 'The standard all-grass-type bag this guide covers: straight feeding with no weed control, which is exactly why it fits the flexible season windows in the table below.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41-eKeqethL'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'The spreader settings in this section come straight from the Scotts bag, and the EdgeGuard Mini is the spreader those settings are written for.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'when-is-the-best-time-to-apply-fungicide-to-lawn': [
    {
      name: 'REOTEMP Waterproof Soil Thermometer, 5" Stem',
      affiliateUrl: amazonUrl('B07F93K8ZY'),
      price: '12.99',
      description: 'Fungal windows open on soil temperature, not the calendar. Brown patch pressure starts when nights stay warm and soil holds the mid 60s, so a stem thermometer tells you when your preventive window actually opens.',
      insertAfterH2: 2,
      imageUrl: amazonImg('21ZMzIf-j4L'),
      badge: { label: 'Know Your Window', tone: 'blue' },
    },
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The standard preventive play this section describes: a granular azoxystrobin you spread before disease shows, with protection that starts in 24 hours and holds up to 4 weeks.',
      insertAfterH2: 3,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'Best Preventive', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'BioAdvanced Fungus Control for Lawns - 32 fl oz Bottle',
      affiliateUrl: amazonUrl('B0035KNZJU'),
      price: '16.98',
      description: 'The curative option for when disease is already visible: a hose-end spray with systemic propiconazole that knocks back active brown patch and dollar spot between granular applications.',
      insertAfterH2: 7,
      imageUrl: amazonImg('41i4wnRkw-L'),
      badge: { label: 'Best Curative Spray', tone: 'green' },
    },
  ],

  'can-fungicide-help-your-lawn': [
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'If your lawn has an active fungal disease, this is the yes: a granular azoxystrobin that covers most common lawn diseases, starts working in 24 hours, and spreads with the same spreader you already own.',
      insertAfterH2: 2,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'The Standard Answer', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'BioAdvanced Fungus Control for Lawns - 32 fl oz Bottle',
      affiliateUrl: amazonUrl('B0035KNZJU'),
      price: '16.98',
      description: 'The beginner-friendly liquid route: clips onto a garden hose, no mixing or measuring, and the systemic propiconazole both cures visible disease and protects for up to a month.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41i4wnRkw-L'),
      badge: { label: 'Easiest to Apply', tone: 'blue' },
    },
  ],

  'brown-patch-disease-fescue-diagnosis-organic-treatment-options': [
    {
      name: 'GS Plant Foods Liquid Kelp Fertilizer',
      affiliateUrl: amazonUrl('B089HSJQ4Q'),
      price: '39.95',
      description: 'The organic backbone of this treatment plan. Sea kelp strengthens cell walls and stress tolerance so fescue resists fungal pressure on its own, without feeding the nitrogen surge that brown patch loves.',
      insertAfterH2: 4,
      imageUrl: amazonImg('71F62TboR3L'),
      badge: { label: 'Best Organic Option', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The conventional backstop if organic measures cannot hold a bad outbreak: a granular azoxystrobin to stop active brown patch fast, then return to the cultural program for prevention.',
      insertAfterH2: 5,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: "If Organic Isn't Enough", tone: 'slate' },
    },
  ],

  // 2026-08-18 audit: the WeedClear editorial card duplicated the page's own
  // 2-card grid (redundant second display of the same product) and the page had
  // no heat-safe option despite an H2 about spraying above 85°F, and no sedge
  // product despite the H1 promising nutsedge. Celsius + Sedgehammer ASINs/
  // prices/images reused from the verified bermuda/zoysia weed-control entries.
  'summer-weed-management': [
    {
      name: 'Celsius WG Herbicide (Easy Mix Packet)',
      affiliateUrl: amazonUrl('B0BTN1DPMD'),
      price: '15.20',
      description: 'The herbicide built for exactly this problem: selective control of 150+ weeds, labeled for Bermuda, Zoysia, and St. Augustine, with no temperature ceiling on the herbicide itself when everything else on the shelf says wait for fall. Skip the spray adjuvant above 90°F, the one heat rule its label does set.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41hwVWk83-L'),
      badge: { label: 'Safe Above 85°F', tone: 'gold', tier: 'editor' },
      shortName: 'Celsius WG',
    },
    {
      name: 'Sedgehammer Plus Turf Solution',
      affiliateUrl: amazonUrl('B007PHMAYK'),
      price: '13.99',
      description: 'Nutsedge shrugs off broadleaf killers entirely. Selective halosulfuron takes out yellow and purple nutsedge without harming the lawn around it.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51w5G6PEFFL'),
      badge: { label: 'For the Nutsedge', tone: 'green' },
      shortName: 'Sedgehammer',
    },
    {
      name: 'Spectracide Weed Stop For Lawns Concentrate (32 oz)',
      affiliateUrl: amazonUrl('B07K78HDDS'),
      price: '7.47',
      description: 'The budget concentrate for the action plan: mix your own tank strength and cover large areas of broadleaf weeds for a fraction of the ready-to-spray price.',
      insertAfterH2: 6,
      imageUrl: amazonImg('41oLm58LEiL'),
      badge: { label: 'Budget Concentrate', tone: 'slate' },
    },
  ],

  // Branch-matched pick set: this post routes readers to three different chemistries
  // depending on what they are clearing, so each card sits under the section that
  // makes its case. disableLeadAnchor keeps the verdict card at the comparison
  // section instead of snapping it to the intro, where it would pre-empt the
  // comparison the reader came for. H2 order verified against Directus post 433.
  // ASINs/prices/images PAAPI-verified 2026-08-15.
  'ortho-groundclear-vs-roundup-which-to-use': [
    {
      // H2 #3 "GroundClear vs Roundup: The Real Comparison"
      name: 'Ortho GroundClear Year Long Vegetation Killer2 Concentrate, 1 gal.',
      affiliateUrl: amazonUrl('B0CJL7Q92X'),
      price: '31.47',
      description: 'The GroundClear side of the comparison: a residual concentrate that clears a driveway, gravel strip, or fence line and holds it bare for the season. Never use it anywhere you want plants to grow back.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41ZJtJJ5uoL'),
      badge: { label: 'Best for driveways & fence lines', tone: 'gold', rating: 4.5, tier: 'verdict' },
      shortName: 'GroundClear Year Long',
      disableLeadAnchor: true,
    },
    {
      // H2 #7 "When Can I Reseed or Replant After GroundClear?"
      name: 'Compare-N-Save Concentrate Glyphosate Grass & Weed Killer, 32 oz',
      affiliateUrl: amazonUrl('B00ARKS23A'),
      price: '22.99',
      description: 'If you plan to reseed or replant, this is the product the section above points you to: 41% glyphosate has no meaningful soil residual once dry, so the ground is plantable in days instead of a season.',
      insertAfterH2: 7,
      imageUrl: amazonImg('41HuJN+ZfcL'),
      badge: { label: "Best if you're replanting", tone: 'blue', tier: 'editor' },
      shortName: 'Compare-N-Save Glyphosate',
    },
    {
      // H2 #9 "Your GroundClear Action Plan" (last section; resolves to the
      // end-of-content anchor since there is no H2 #10 to sit above).
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'If the weeds are in your lawn, neither GroundClear nor Roundup is the answer: both kill grass. WeedClear is the Ortho product made for that job, killing broadleaf weeds while the turf stays green.',
      insertAfterH2: 2,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'For weeds IN the lawn', tone: 'green', tier: 'pick' },
      shortName: 'Ortho WeedClear',
    },
  ],

  'lawn-turning-yellow-causes-fixes': [
    {
      name: 'Pennington Ironite All-Purpose 1-0-1 Lawn & Garden Fertilizer',
      affiliateUrl: amazonUrl('B07FSV88HS'),
      price: '42.20',
      description: 'Pale yellow-green across the whole lawn is often iron, not nitrogen. Ironite deepens color fast without pushing growth, so it fixes chlorosis yellowing without the burn risk.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51qEgPQgN0L'),
      badge: { label: 'Fixes Iron Yellowing', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'For the patchy yellow circles this section covers: a broad-spectrum granular fungicide that stops brown patch and other common lawn diseases within 24 hours of watering in.',
      insertAfterH2: 2,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'For Patchy Yellow Spots', tone: 'blue' },
    },
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'If fertilizer burn caused the yellowing, switch to a feed that cannot repeat it: slow-release organic nitrogen greens the lawn back up with no burn risk even on hot days.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'Burn-Proof Recovery Feed', tone: 'green' },
    },
  ],

  'zoysia-grass-diseases-identification-treatment': [
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The azoxystrobin homeowner fungicide that covers large patch, the number one zoysia disease. Apply at the curative rate when rings are active, or preventively in the fall window before soil temps drop.',
      insertAfterH2: 2,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'Covers Large Patch', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Cultural prevention starts with knowing your numbers. Over-fertilized nitrogen is a top driver of zoysia disease, and a mail-in test tells you what your lawn actually needs instead of guessing.',
      insertAfterH2: 8,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Prevention Starts Here', tone: 'blue' },
    },
  ],

  'st-augustine-grass-diseases-identification-treatment': [
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The azoxystrobin homeowner fungicide that covers brown patch and gray leaf spot, the two big St. Augustine diseases in this breakdown. Granular, spreader-applied, and rain-safe once watered in.',
      insertAfterH2: 2,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'Covers the Big Two', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Disease-prone St. Augustine is often over-fed St. Augustine. A mail-in soil test shows whether your nitrogen program is feeding the grass or the fungus, so cultural prevention has real numbers behind it.',
      insertAfterH2: 4,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Prevention Starts Here', tone: 'blue' },
    },
  ],

  'lawn-rust-disease-orange-grass-treatment': [
    {
      name: 'Pennington Full Season Lawn Fertilizer 32-0-5',
      affiliateUrl: amazonUrl('B0DYWRC5NM'),
      price: '27.99',
      description: 'Rust is a hungry-lawn disease, and the fix is usually nitrogen, not chemicals. A high-nitrogen 32-0-5 feeding pushes new growth fast enough that you mow the rust out within a few weeks.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51wvj2XoEQL'),
      badge: { label: 'The Rust Fix: Feed It', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts DiseaseEx Lawn Fungicide',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'For the rare severe case where feeding and mowing are not turning it around. Azoxystrobin covers rust on the label, but treat it as the backup plan this section describes, not the first move.',
      insertAfterH2: 6,
      imageUrl: amazonImg('710N1nUPNFL'),
      badge: { label: 'Backup Plan Only', tone: 'slate' },
    },
  ],

  'ortho-weedclear-lawn-weed-killer-how-to-use': [
    {
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'The product this guide walks through: a ready-to-spray broadleaf killer that screws onto a garden hose, no mixing, and clears dandelion, clover, and chickweed while the lawn stays green.',
      insertAfterH2: 4,
      // The card belongs at the mixing/application section this guide builds up to,
      // not snapped to the near-top lead anchor.
      disableLeadAnchor: true,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
  ],

  'ironite-for-lawns-application-guide': [
    {
      name: 'Pennington Ironite All-Purpose 1-0-1 Lawn & Garden Fertilizer',
      affiliateUrl: amazonUrl('B07FSV88HS'),
      price: '42.20',
      description: 'The product this guide covers: a mineral supplement that deepens color fast with iron and no added nitrogen, so the lawn greens up without the growth spurt or the burn risk.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51qEgPQgN0L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
  ],

  'scotts-pre-emergent-apply-application-rate-2026-schedule': [
    {
      name: 'Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.',
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: 'The Scotts pre-emergent this schedule is built around: a spring application that stops crabgrass and listed grassy weeds before they germinate, timed to soil temperature rather than the calendar.',
      insertAfterH2: 4,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Every application rate in this guide assumes a calibrated broadcast spreader. The EdgeGuard Mini uses the Scotts settings printed on the Halts bag and blocks the spread pattern at edges to keep granules off beds and pavement.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'roundup-for-lawns-application-guide-vs-regular-roundup': [
    {
      name: 'Roundup for Lawns Concentrate',
      affiliateUrl: amazonUrl('B09NCJ2ZXQ'),
      price: '20.46',
      description: 'The lawn-safe Roundup this guide covers: a selective formula for northern grasses that kills crabgrass, dandelion, and clover without the glyphosate that makes regular Roundup fatal to turf.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41P9IJ+x2sL'),
      badge: { label: 'The Lawn-Safe One', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'The main alternative in this section: the same selective broadleaf control in a hose-end bottle at a lower price, with a label that covers more southern grass types.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'Best Alternative', tone: 'blue' },
    },
  ],

  // Herbicides from the post's own tolerance matrix, one per weed group. Each
  // label lists St. Augustinegrass (Hi-Yield 33030 label: dithiopyr; Ortho
  // SKU 4665201: sulfentrazone). No quinclorac: UF rates it damaging here.
  // ASINs, prices and images PAAPI-verified 2026-09-16.
  'weeds-in-st-augustine-grass': [
    {
      name: 'Southern Ag Atrazine St. Augustine Weed Killer (32 oz)',
      affiliateUrl: amazonUrl('B0052MZBYQ'),
      price: '15.99',
      description: 'Atrazine is the ingredient UF rates tolerant on St. Augustine for doveweed and dollarweed. One quart treats 3,720 sq ft; plan on repeat applications.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41xJ0HypHcL'),
      badge: { label: 'Broadleaf weeds', tone: 'green', tier: 'pick' },
      shortName: 'Southern Ag Atrazine',
    },
    {
      name: 'Ortho Nutsedge Killer for Lawns Ready-To-Spray (32 oz)',
      affiliateUrl: amazonUrl('B0FNXBGZNS'),
      price: '21.99',
      description: 'Sulfentrazone in a hose-end sprayer whose label lists St. Augustinegrass. Fix wet soil first, and expect to repeat on established nutsedge.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41itmngwVvL'),
      badge: { label: 'Nutsedge', tone: 'blue', tier: 'pick' },
      shortName: 'Ortho Nutsedge Killer',
    },
    {
      name: 'Hi-Yield Turf & Ornamental Weed & Grass Stopper (12 lb)',
      affiliateUrl: amazonUrl('B005UJMGO4'),
      price: '27.94',
      description: 'Granular dithiopyr pre-emergent labeled for St. Augustinegrass. Spread it before crabgrass and goosegrass germinate; the bag covers 5,000 sq ft.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41NcCkAVEwL'),
      badge: { label: 'Crabgrass preventer', tone: 'gold', tier: 'pick' },
      shortName: 'Hi-Yield Grass Stopper',
    },
  ],

  // One pick per sticker window. Grass lists come from each product label:
  // Andersons Barricade 18 lb label (prodiamine 0.48%), Southern Ag Atrazine
  // St. Augustine Weed Killer label (spurweed and sandspur listed), Scotts
  // Halts listing (pendimethalin per K-State). No quinclorac or Katana picks:
  // both labels restrict St. Augustine. ASINs, prices and images
  // PAAPI-verified 2026-09-17.
  'how-to-get-rid-of-stickers-in-yard': [
    {
      name: 'Southern Ag Atrazine St. Augustine Weed Killer (32 oz)',
      affiliateUrl: amazonUrl('B0052MZBYQ'),
      price: '15.99',
      description: 'Atrazine labeled for established St. Augustine and centipede, with spurweed (lawn burweed) and sandspur on its weed list. The label says spurweed control is best when the weed has newly emerged, in December and January.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41xJ0HypHcL'),
      badge: { label: 'Winter burweed spray', tone: 'green', tier: 'pick' },
      shortName: 'Southern Ag Atrazine',
    },
    {
      name: 'The Andersons Barricade Professional-Grade Granular Pre-Emergent Weed Control (18 lb)',
      affiliateUrl: amazonUrl('B07NZ2BMMK'),
      price: '46.88',
      description: 'Granular prodiamine, one of the three pre-emergents UGA names for lawn burweed, for the late September to mid-October window. The label lists St. Augustine, centipede, zoysia, bermuda, and bahia, but not burweed, so this pick rests on UGA\'s recommendation.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41goftL2ddL'),
      badge: { label: 'Fall burweed pre-emergent', tone: 'gold', tier: 'pick' },
      shortName: 'Andersons Barricade',
    },
    {
      name: 'Scotts Halts Crabgrass & Grassy Weed Preventer (20.12 lb)',
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: 'Pendimethalin, the ingredient K-State and Texas A&M name for sandbur and Colorado State names for goatheads. Listed for all grass types except dichondra and bentgrass. One bag covers 10,000 sq ft.',
      insertAfterH2: 7,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: 'Sandbur and goathead preventer', tone: 'blue', tier: 'pick' },
      shortName: 'Scotts Halts',
    },
  ],

  // Directus 538. Keep this species page on one narrow, post-emergent spot-treatment pick.
  // The article does not endorse a universal post-emergent: turf tolerance and
  // puncturevine listing vary by label. The category fallback was able to inject
  // starter fertilizer or a grassy-weed product that did not match this article.
  // Use the Ortho lawn formulation whose manufacturer label lists goathead and
  // puncture vine, and still require the reader to verify the current label.
  'goatheads-in-grass': [
    {
      name: 'Ortho Weed B Gon Weed Killer, 24oz, RTU Trigger',
      affiliateUrl: amazonUrl('B00MKB5YHG'),
      price: '18.25',
      description: 'A broadleaf spot-treatment option whose manufacturer listing includes goathead and puncture vine. Confirm that the current label lists your grass and site, then follow every label instruction before applying.',
      insertAfterH2: 7,
      imageUrl: amazonImg('41Qh1u57N9L'),
      badge: { label: 'Label-listed spot spray', tone: 'green', tier: 'pick' },
      shortName: 'Ortho Weed B Gon',
    },
  ],

  // Directus 537. The post recommends Certainty (sulfosulfuron) on bermuda,
  // zoysia, St. Augustine, and centipede per its EPA label, and a glyphosate spot
  // spray on tall fescue or bluegrass. ASINs checked with paapi-asin-lookup on
  // 2026-09-18 (Certainty from data/amazon-product-enrichment.ts; the other two
  // reused from verified entries above).
  'johnson-grass': [
    {
      name: 'Certainty Turf Herbicide (Sulfosulfuron), 1.25 oz',
      affiliateUrl: amazonUrl('B004W4SMUA'),
      price: '134.51',
      description: 'Sulfosulfuron, labeled for residential turf and listing Johnsongrass on bermuda, zoysia, St. Augustine, and centipede at 1.25 to 2.0 ounces of product per acre. Mix it with a nonionic surfactant, and test St. Augustine on a small area first.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41zabvjrK3L'),
      badge: { label: 'Warm-season lawns', tone: 'gold', tier: 'pick' },
      shortName: 'Certainty Turf Herbicide',
    },
    {
      name: 'Compare-N-Save Concentrate Glyphosate Grass & Weed Killer, 32 oz',
      affiliateUrl: amazonUrl('B00ARKS23A'),
      price: '22.99',
      description: 'For tall fescue and bluegrass lawns: a glyphosate concentrate for spot-spraying Johnson grass clumps, timed by Illinois Extension for plants at least 18 inches tall and before flowering. It kills any grass it touches, lawn included, so keep it on the clump.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41HuJN+ZfcL'),
      badge: { label: 'Johnson grass spot spray', tone: 'green', tier: 'pick' },
      shortName: 'Compare-N-Save Glyphosate',
    },
    {
      name: 'Chapin 20084 1-Gallon Pump Sprayer (4-in-1 Nozzle)',
      affiliateUrl: amazonUrl('B0F2BZX4YR'),
      price: '24.95',
      description: 'A one-gallon hand-pump sprayer with a 4-in-1 nozzle lets you treat one clump at a time while you shield the turf around it.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41Mbdp1ifZL'),
      badge: { label: 'To spot-spray it', tone: 'slate' },
      shortName: 'Chapin Pump Sprayer',
    },
  ],

  'best-weed-feed-st-augustine-grass-2026': [
    {
      name: 'Scotts Turf Builder Southern Lawn Food',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'The feed half of the safest St. Augustine strategy: standard weed and feed 2,4-D blends damage St. Augustine, so feed with a Southern-formulated bag and treat weeds separately.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'Safe Feed Half', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'The weed half: a spot-treatment spray whose label covers St. Augustine, so you hit the actual weeds instead of blanketing the whole lawn with herbicide it does not tolerate.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'St. Augustine-Safe Weed Control', tone: 'blue' },
    },
  ],

  'best-fertilizer-for-bermuda-grass-in-north-carolina': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 that fits North Carolina bermuda: steady nitrogen for color, light phosphorus and potassium for roots, plus humic DG that helps on both Piedmont clay and Coastal Plain sand.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Overall for NC', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'A 16-4-8 liquid for quick green-up between granular feedings, easy to apply evenly through a hose-end sprayer across North Carolina\'s long bermuda season.',
      insertAfterH2: 4,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'Best Liquid Option', tone: 'blue' },
    },
    {
      name: 'Pennington Full Season Lawn Fertilizer 32-0-5',
      affiliateUrl: amazonUrl('B0DYWRC5NM'),
      price: '27.99',
      description: 'The budget high-nitrogen route: a 32-0-5 with iron whose controlled release carries color through the peak summer push without repeat applications.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51wvj2XoEQL'),
      badge: { label: 'Budget High-Nitrogen', tone: 'slate' },
    },
  ],

  'andersons-pgf-complete-16-4-8-application-guide': [
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The bag this guide covers: a professional-grade 16-4-8 with humic DG and particles fine enough that every square foot gets fed, which is exactly why the rates in this schedule work.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The comparison in this section: slower, gentler organic nitrogen at a lower price, the better fit if you want zero burn risk over PGF\'s faster color.',
      insertAfterH2: 9,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'The Alternative Compared', tone: 'green' },
    },
  ],

  'scotts-southern-weed-and-feed-bonus-s-instructions-timing': [
    {
      name: 'BioAdvanced 3-in-1 Weed and Feed for Southern Lawns (10 lb)',
      affiliateUrl: amazonUrl('B0BR8J59CL'),
      price: '30.98',
      description: 'Bonus S itself is scarce on Amazon, and this is the closest buyable southern weed and feed for St. Augustine and bermuda. Centipede lawns should stick with Bonus S from a local store: its atrazine is the safer chemistry there.',
      insertAfterH2: 4,
      imageUrl: amazonImg('512ZbA96MxL'),
      badge: { label: 'Buyable Alternative', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'The application steps here assume a calibrated broadcast spreader, and the EdgeGuard Mini matches the Scotts settings printed on the Bonus S bag.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'which-simple-lawn-solutions-to-buy': [
    {
      name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
      affiliateUrl: amazonUrl('B06XWFL84R'),
      price: '23.77',
      description: 'The default this guide lands on: a balanced 16-4-8 with seaweed and humic acid that works on all common lawn grasses through the attached hose-end sprayer.',
      insertAfterH2: 2,
      imageUrl: amazonImg('419+STgxhSL'),
      badge: { label: 'The Default Pick', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Simple Lawn Solutions Superior 15-0-15 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9L1GW5'),
      price: '24.77',
      description: 'The summer specialist: phosphorus-free with high potassium for heat and stress hardiness, and the right call in phosphorus-restricted areas.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41wW+L4asjL'),
      badge: { label: 'The Summer Specialist', tone: 'green' },
    },
    {
      name: 'Simple Lawn Solutions Maximum Green 28-0-0 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9Z9MQR'),
      price: '24.79',
      description: 'The fast green-up: high-nitrogen 28-0-0 with a quick and slow-release blend when color is the whole mission.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41drKSOTKCL'),
      badge: { label: 'The Fast Green-Up', tone: 'blue' },
    },
  ],

  'granular-pre-emergent-herbicide-timing-application-guide': [
    {
      name: 'Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.',
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: 'The widely available granular pre-emergent for the timing windows in this guide: pendimethalin that stops crabgrass and listed grassy weeds before germination, spread with any broadcast spreader.',
      insertAfterH2: 2,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: 'Easiest to Find', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'The calibration section assumes a broadcast spreader you can dial in. The EdgeGuard Mini holds a consistent rate and shuts off the pattern at edges, which matters more with herbicides than fertilizer.',
      insertAfterH2: 8,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Calibration Matters', tone: 'slate' },
    },
  ],

  'best-fertilizer-centipede-grass-2026-guide': [
    {
      name: 'Ferti-Lome Centipede Lawn Fertilizer 15-0-15 (20 lb)',
      affiliateUrl: amazonUrl('B01LIIWPFK'),
      price: '36.07',
      description: 'The 15-0-15 ratio this guide recommends for centipede: no phosphorus, high potassium for stress tolerance, covers about 5,000 sq ft. Centipede is a low-feed grass, so one or two of these a year is plenty.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41CRNNyjL+L'),
      badge: { label: 'Best for Centipede', tone: 'gold' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Centipede is sensitive to pH and over-feeding, so test before you fertilize. The lab report gives your exact P and K levels and whether lime or sulfur is needed.',
      insertAfterH2: 3,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Apply granular centipede fertilizer evenly with a calibrated broadcast spreader. Uneven hand-spreading is a common cause of streaky green-up.',
      insertAfterH2: 4,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'best-fertilizer-for-bermuda-grass-top-picks-reviews': [
    // 2026-07-01 retier (affiliate revenue plan): the guide's #1 pick leads as the
    // verdict card (it already earns the most in-content clicks on this page).
    // PAAPI-verified in-stock 2026-07-01.
    {
      name: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer with 7% Humic DG 5,000 sq ft',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'The #1 pick in this guide: a textbook 4-1-2 ratio for Bermuda with 7% humic DG, and super-fine particles that lay down twice as many granules per square foot for an even feed.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Verdict', tone: 'gold', rating: 4.7, tier: 'verdict' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'This guide bases its NPK picks on your soil test. MySoil mails back exact P, K, and pH so you can match the right ratio (a high-K 15-0-15 vs a balanced feed) instead of guessing.',
      insertAfterH2: 2,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Bermuda is fed by the pound of nitrogen per 1,000 sq ft, which only works if a calibrated spreader lays it down evenly. The EdgeGuard Mini handles the granular picks in this guide.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'best-fertilizer-for-tall-fescue-2026-guide': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Tall fescue has different NPK and pH needs from lawn to lawn, and the fall-heavy schedule only pays off if you feed the right ratio. MySoil mails back exact P, K, and pH so you match the product to your soil instead of guessing.',
      insertAfterH2: 3,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Tall fescue is fed by the pound of nitrogen per 1,000 sq ft, which only works if a calibrated spreader lays the granular picks down evenly. The EdgeGuard Mini shuts off the spread pattern at edges to keep product off beds and pavement.',
      insertAfterH2: 7,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
    {
      name: 'Scotts Turf Builder Starter Food for New Grass',
      affiliateUrl: amazonUrl('B00DK49DVA'),
      price: '34.97',
      description: 'Tall fescue is a bunch grass that thins and needs overseeding, ideally in early fall. A high-phosphorus starter drives root growth in new seedlings for the first few weeks before you switch back to your normal feed.',
      insertAfterH2: 8,
      imageUrl: amazonImg('81oY9zpw+qL'),
      badge: { label: 'Best for Overseeding', tone: 'green' },
    },
  ],

  // 2026-08-18 audit: the page's stated thesis is iron ("the unsung hero" of a
  // Texas plan) yet every configured pick was gear — and the fertilizer cards
  // live in the hand-authored top grid. Iron card takes the H2-2 slot at the
  // end of the NPK section (and leads the array for the sidebar rail); the
  // never-clicked Dial N Spray is gone; the soil-test kit yields its slot to
  // the page's one uncovered purchase. Iron ASIN reused from the verified
  // best-liquid-iron entry.
  'best-fertilizer-for-bermuda-grass-in-texas': [
    {
      name: 'Simple Lawn Solutions Liquid Iron Darker Green (32 oz)',
      affiliateUrl: amazonUrl('B08Z4DTTJR'),
      price: '23.99',
      description: 'Chelated iron stays available in high-pH Texas soil where plain iron sulfate locks up. Deep-greens Bermuda west of I-35 without pushing growth in August heat.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51OgZKkTOPL'),
      badge: { label: 'Fixes Texas Yellowing', tone: 'gold' },
      shortName: 'SLS Liquid Iron',
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Texas Bermuda gets fed several times across a long season. A calibrated broadcast spreader makes each pass even and keeps product off hot pavement.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],
  // 96%-bounce commercial post with zero in-content links until 2026-07-13.
  // Two products only, mirroring the article's own sequence: test first, then
  // pelletized lime. Catalog spreaders (B002YPS1KK, B011HY5Q1K) were buy-box
  // OOS at authoring time and left out.
  'when-to-apply-lime-to-lawn': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Lime only helps acidic soil, and the right rate lives on a soil test report. Mail-in kit that returns your pH and specific recommendations before you buy a single bag.',
      insertAfterH2: 2,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
      shortName: 'MySoil Test Kit',
    },
    {
      name: 'Encap Fast Acting Pelletized Lime (25 lb)',
      affiliateUrl: amazonUrl('B0924ZZT5C'),
      price: '36.99',
      description: 'Pelletized lime that flows cleanly through a broadcast spreader with almost no dust. Fast-acting formula starts moving pH sooner than coarse agricultural lime.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51gvJl9gbfL'),
      badge: { label: 'Best Pelletized Lime', tone: 'green' },
      shortName: 'Encap Fast Acting Lime',
    },
  ],
  // 91%-bounce install guide with zero in-content links until 2026-07-13. The
  // catalog has no turf-install inventory, so these were sourced fresh and
  // PAAPI-verified in stock. Both map to the guide's own steps: seam tape
  // (Step 7), perimeter nails (Step 8). Post has 6 H2s; all install steps are
  // H3s under H2 #4.
  'artificial-grass-installation-step-by-step-guide-2026': [
    {
      name: 'TYLife Artificial Grass Seaming Tape (6" x 65.6\')',
      affiliateUrl: amazonUrl('B07KNJVQQ8'),
      price: '40.22',
      description: 'Self-adhesive seam tape for joining turf pieces so the seam disappears. Waterproof and UV resistant, rated for 6 to 8 years after installation.',
      insertAfterH2: 3,
      imageUrl: amazonImg('61yPT4woSRL'),
      badge: { label: 'For Invisible Seams', tone: 'green' },
      shortName: 'TYLife Seaming Tape',
    },
    {
      name: 'One Stop Outdoor 5.5" Galvanized Turf Nails (150 Pack)',
      affiliateUrl: amazonUrl('B078XTCJDM'),
      price: '26.99',
      description: 'USA-made galvanized spikes for securing turf edges and seams, one every 4 to 6 inches along the perimeter. The 5.5 inch length holds in compacted base rock.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41wpD8H8G7L'),
      badge: { label: 'Secures Every Edge', tone: 'slate' },
      shortName: 'One Stop Turf Nails',
    },
  ],

  // Regional reuse of the verified Texas-Bermuda ASINs (soil-test lead + spreader),
  // surfaced early per the intent-aware placement. Body keeps its inline fertilizer links.
  'best-fertilizer-for-bermuda-grass-in-georgia': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Georgia Bermuda runs on the right NPK ratio, and that depends on your soil. MySoil mails back exact P, K, and pH so you match the feed to your lawn instead of guessing.',
      insertAfterH2: 1,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Bermuda is fed by the pound of nitrogen per 1,000 sq ft, which only works with a calibrated spreader laying it down evenly. The EdgeGuard Mini handles the granular picks in this guide.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'best-fertilizer-for-bermuda-grass-in-florida': [
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'Florida Bermuda feeds nearly year-round on sandy soils that leach nutrients fast. A MySoil test shows exactly what your lawn is short on before you spend on fertilizer.',
      insertAfterH2: 1,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'With many feedings across a long Florida season, a calibrated spreader keeps each pass even and keeps product off hot pavement and out of beds.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'tenacity-herbicide-mesotrione-application-guide': [
    {
      name: 'Syngenta Tenacity Turf Herbicide (8 oz)',
      affiliateUrl: amazonUrl('B005DUTNF0'),
      price: '62.06',
      description: 'The product this guide is about. A little goes a long way: 8 oz treats a large lawn at the label rate, controlling listed broadleaf and grassy weeds both pre- and post-emergent.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41tEE7n3sRL'),
      badge: { label: 'The Herbicide', tone: 'gold', tier: 'editor' },
    },
    {
      // Mistake 7 in this guide tells readers never to apply Tenacity through a
      // hose-end sprayer, so the pick must be a hand-pump unit. PAAPI-verified 2026-08-15.
      name: 'Chapin 20084 1-Gallon Pump Sprayer (4-in-1 Nozzle)',
      affiliateUrl: amazonUrl('B0F2BZX4YR'),
      price: '24.95',
      description: 'Tenacity is sprayed, not spread, and never through a hose-end sprayer. A one-gallon hand-pump sprayer set to a flat-fan pattern gives the measured, even coverage the label calls for.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41Mbdp1ifZL'),
      badge: { label: 'To Spray It', tone: 'slate' },
      shortName: 'Chapin Pump Sprayer',
    },
    {
      name: 'Southern Ag Non-Ionic Surfactant',
      affiliateUrl: amazonUrl('B004XDHRCE'),
      price: '14.38',
      description: 'Tenacity needs a non-ionic surfactant for foliar uptake on post-emergent applications. This pint is the standard, inexpensive add-in.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51fBawyDg7L'),
      badge: { label: 'Required Add-In', tone: 'blue' },
    },
  ],

  'best-fertilizer-for-st-augustine-grass-2026-guide': [
    // 2026-07-01 retier (affiliate revenue plan): the money SKU leads as the verdict
    // card; the page previously carried only accessories while in-content links did
    // all the fertilizer selling. PAAPI-verified in-stock 2026-07-01.
    {
      name: 'Scotts Lawn Fertilizer - Southern Turf Builder for Southern Grass - 15,000 Sq. Ft. - Granules',
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: 'The straightforward St. Augustine feed: formulated for Southern turf, safe at label rates in Florida and Texas heat, and the 15,000 sq ft bag covers most lawns for a full season of feedings.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: 'Verdict', tone: 'gold', rating: 4.5, tier: 'verdict' },
    },
    {
      name: 'MySoil Soil Test Kit',
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: 'St. Augustine yellows from iron and pH issues as often as from low nitrogen. A soil test tells you which, so you buy the right product instead of over-feeding.',
      insertAfterH2: 1,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: 'Test First', tone: 'blue' },
    },
    {
      // 2026-08-18 audit: this page ranks position ~1.2 at 25% CTR for "best
      // iron supplement for st augustine grass" and sold nothing against it —
      // the iron card closes the Types-of-Fertilizer section that houses the
      // "Iron Supplements: When and Why" H3. Replaces the never-clicked Dial N
      // Spray. ASIN reused from the verified best-liquid-iron entry.
      name: 'Simple Lawn Solutions Liquid Iron Darker Green (32 oz)',
      affiliateUrl: amazonUrl('B08Z4DTTJR'),
      price: '23.99',
      description: 'The iron supplement this section describes: chelated liquid iron that deep-greens St. Augustine and corrects chlorosis yellowing without pushing growth in summer heat.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51OgZKkTOPL'),
      badge: { label: 'Best Iron for St. Augustine', tone: 'gold' },
      shortName: 'SLS Liquid Iron',
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Apply granular St. Augustine fertilizer evenly with a calibrated broadcast spreader to avoid the streaky green-up that uneven hand-spreading causes.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'milorganite-application-guide-best-uses-2026': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The product this guide covers. Slow-release organic nitrogen with iron for deep green without a growth surge, and it will not burn the lawn even in summer heat.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'The Product', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Milorganite goes down at about 1 lb of product per 100 sq ft. A calibrated broadcast spreader makes that rate even and fast.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
    {
      name: 'The Andersons PGF Complete 16-4-8',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'When you want faster green-up or a higher nitrogen rate than Milorganite delivers, this pro-grade 16-4-8 with humic DG is the step-up alternative the guide compares against.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Step-Up Alternative', tone: 'blue' },
    },
  ],

  // === 2026-07-15 affiliate CTR pass: inline-anchor churn post ===
  // This post drew ~220 weekly inline-anchor impressions with zero clicks
  // (PostHog 7d) and had no curated entry, so readers only ever saw prose
  // links. ASINs/prices/images reused from PAAPI-verified sibling entries.
  'milorganite-application-guide-apply': [
    {
      name: 'Milorganite 6-4-0 Slow-Release Nitrogen Fertilizer (32 lb)',
      affiliateUrl: amazonUrl('B0FGDNF55M'),
      price: '35.01',
      description: 'The bag this application guide covers. Slow-release organic nitrogen with iron for deep green without surge growth, and it will not burn the lawn even in summer heat.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51+RWV7fs3L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Milorganite goes down at about 1 lb of product per 100 sq ft. A calibrated broadcast spreader makes that rate even and fast.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  // === 2026-06-19 weed-type overrides ===
  // The dynamic CategoryProductCallout keyword-ranked a broadleaf killer (Ortho
  // WeedClear) onto grassy-weed and sedge posts where it does not work, contradicting
  // the article. These curated picks suppress that fallback with the correct product per
  // weed type. ASINs PAAPI-verified in-stock 2026-06-19. See obsidian/changelog/2026-06.md.
  'how-to-get-rid-of-nutsedge': [
    {
      name: 'Sedgehammer Plus Turf Solution',
      affiliateUrl: amazonUrl('B007PHMAYK'),
      price: '13.99',
      description: 'Nutsedge shrugs off regular weed and feed. Sedgehammer\'s selective halosulfuron kills yellow and purple nutsedge without wrecking your lawn.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51w5G6PEFFL'),
      badge: { label: 'Best for Nutsedge', tone: 'green' },
    },
  ],

  'how-to-get-rid-of-dallisgrass': [
    {
      name: 'Ortho Weed B Gon Plus Crabgrass Control Ready-To-Spray',
      affiliateUrl: amazonUrl('B005LD2GNK'),
      price: '24.99',
      description: 'For young dallisgrass and crabgrass, this ready-to-spray quinclorac formula targets the grassy weeds most broadleaf killers miss. Spot-treat established clumps separately.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41B5D141ytL'),
      badge: { label: 'Best for Grassy Weeds', tone: 'green' },
    },
  ],

  'how-to-get-rid-of-wild-violet-in-lawn': [
    {
      name: 'Bonide Chickweed, Clover & Oxalis Killer (Ready-to-Use)',
      affiliateUrl: amazonUrl('B000HCRM4S'),
      price: '18.59',
      description: 'Wild violet\'s waxy leaves shrug off standard sprays. This formula is built for the tough, waxy broadleaf weeds like wild violet, clover, oxalis, and creeping charlie.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51sqdjbQXZS'),
      badge: { label: 'Best for Tough Broadleaf', tone: 'green' },
    },
  ],

  'how-to-get-rid-of-spotted-spurge': [
    {
      name: 'Bonide Chickweed, Clover & Oxalis Killer (Ready-to-Use)',
      affiliateUrl: amazonUrl('B000HCRM4S'),
      price: '18.59',
      description: 'Knocks down spotted spurge and other low broadleaf weeds like oxalis and clover, roots and all, without harming listed lawn grasses. Pair with a spring pre-emergent to stop next year\'s flush.',
      insertAfterH2: 3,
      imageUrl: amazonImg('51sqdjbQXZS'),
      badge: { label: 'Best for Broadleaf Weeds', tone: 'green' },
    },
  ],

  'how-to-get-rid-of-poa-annua': [
    {
      name: 'Quali-Pro Prodiamine 65 WDG Pre-Emergent',
      affiliateUrl: amazonUrl('B004GTQBEK'),
      price: '77.00',
      description: 'Poa annua is beaten with a fall pre-emergent. Prodiamine lays down a barrier that stops poa annua and crabgrass before they germinate, with up to 6 months of control.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41memD5pcmL'),
      badge: { label: 'Best Pre-Emergent', tone: 'blue' },
    },
  ],

  'scotts-weed-feed-actually-work-honest-review': [
    {
      name: 'Scotts Turf Builder Weed and Feed 3',
      affiliateUrl: amazonUrl('B00XJM8KMQ'),
      price: '64.99',
      description: 'Works well on established cool-season lawns with active broadleaf weeds at 55°F+ soil temps. The 2,4-D + MCPA + Mecoprop-p blend kills dandelions and clover reliably when timing matches active growth.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41IVt6FtH2L'),
      badge: { label: 'Verdict', tone: 'gold', rating: 4.2, tier: 'verdict' },
    },
    {
      name: 'Scotts Turf Builder Triple Action',
      affiliateUrl: amazonUrl('B09JVFT7N5'),
      price: '37.52',
      description: "If crabgrass is also a problem, Triple Action adds a pre-emergent layer. Better fit when you're applying early enough in spring (soil temps near 55°F) and want one bag that handles weeds + crabgrass + feed.",
      insertAfterH2: 5,
      imageUrl: amazonImg('51in7bV-LRL'),
      badge: { label: 'Best Crabgrass Companion', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Whichever Scotts bag you pick, it is granular, so apply it with a calibrated broadcast spreader for even coverage and no fertilizer-burn stripes.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
  ],

  'fastest-growing-grass-varieties': [
    {
      name: 'Pennington Smart Seed Sun and Shade Mix',
      affiliateUrl: amazonUrl('B08QCNV9ZV'),
      price: '60.73',
      description: 'Premium blend with perennial ryegrass for fast germination. Grows in 3x less water.',
      insertAfterH2: 2,
      imageUrl: amazonImg('81NLk-gC4YS'),
    },
    {
      name: 'Scotts Turf Builder Grass Seed Bermudagrass (4 lb)',
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: 'Heat and drought tolerant Bermuda seed for fast coverage in southern lawns. Includes fertilizer and soil improver.',
      insertAfterH2: 4,
      imageUrl: amazonImg('61fLxvlKUwL'),
    },
    {
      name: 'Scotts Turf Builder Starter Food for New Grass, 15 lb',
      affiliateUrl: amazonUrl('B00DK49DVA'),
      price: '34.97',
      description: 'Fast-growing seed establishes faster with a high-phosphorus starter feed. Apply it at seeding to drive root growth through the first three weeks.',
      insertAfterH2: 5,
      imageUrl: amazonImg('81oY9zpw+qL'),
      badge: { label: 'Feed New Seed', tone: 'green' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Spread seed and starter fertilizer evenly with a calibrated broadcast spreader so the new lawn fills in uniformly instead of in clumps.',
      insertAfterH2: 6,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spread It Evenly', tone: 'slate' },
    },
  ],

  'best-lowmaintenance-grass-types': [
    {
      name: 'Jonathan Green Black Beauty Ultra Grass Seed',
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: 'Deep-rooted tall fescue blend that needs less water and fewer inputs once established.',
      insertAfterH2: 3,
      imageUrl: amazonImg('71I5+E4pKxL'),
    },
  ],

  'best-grass-for-hightraffic-areas': [
    {
      name: 'Scotts Turf Builder Thick R Lawn Sun and Shade',
      affiliateUrl: amazonUrl('B077SXJDJP'),
      price: '22.49',
      description: '3-in-1 seed, fertilizer, and soil improver for filling worn patches in high-traffic zones.',
      insertAfterH2: 1,
      imageUrl: amazonImg('71SOTd73k5L'),
    },
  ],

  '10-10-10-fertilizer-when-how-to-apply': [
    {
      name: 'The Andersons PGF Balanced 10-10-10 Fertilizer',
      affiliateUrl: amazonUrl('B08FXR8R75'),
      price: '54.88',
      description: 'True 10-10-10 NPK with micronutrients and 2% iron. Pro-grade DG particles spread evenly and feed turf without phosphorus runoff worries when applied per soil test.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41WDCcdToGL'),
      badge: { label: 'Best true 10-10-10', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'GreenView Multi-Purpose 10-10-10 Fertilizer, 33 lb',
      affiliateUrl: amazonUrl('B00KWYBT0E'),
      price: '29.48',
      description: 'Balanced 10-10-10 in a 33 lb bag — covers about 3,300 sq ft at 1 lb N per 1,000 sq ft. Cost-effective when a soil test confirms balanced deficiency.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41KSeqDMbBL'),
      badge: { label: 'Best Value', tone: 'blue' },
    },
    {
      name: 'Espoma 10-10-10 Garden Food, 6.75 lb',
      affiliateUrl: amazonUrl('B07R2DN7TZ'),
      price: '32.53',
      description: 'Granular 10-10-10 for vegetable beds, shrubs, and flowering plants. Smaller bag size suits landscape beds rather than full-lawn coverage.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51AadHqKAyL'),
      badge: { label: 'Best for Beds', tone: 'green' },
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'Spreading 10-10-10 on a lawn by hand burns streaks where it lands heavy. A calibrated broadcast spreader lays it down at an even, safe rate.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'For Even Lawn Coverage', tone: 'slate' },
    },
  ],

  'methylated-seed-oil-the-complete-guide': [
    {
      name: 'Southern Ag Methylated Seed Oil (MSO) Surfactant (Quart - 32oz)',
      affiliateUrl: amazonUrl('B0149IA526'),
      price: '18.96',
      description: 'The most popular MSO adjuvant for lawn herbicide applications. Improves herbicide absorption and rainfastness.',
      insertAfterH2: 1,
      imageUrl: amazonImg('71LXbBMhR3L'),
      badge: { label: 'Best herbicide surfactant', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'ProSolutions MSO (Methylated Seed Oil) Gallon',
      affiliateUrl: amazonUrl('B0C9MWR5V3'),
      price: '37.99',
      description: 'Gallon-size MSO for serious DIYers and small acreage. Same surfactant function as the quart at lower cost per ounce for tank mixes.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41NfE139YDL'),
      badge: { label: 'Best Value', tone: 'blue' },
    },
    {
      name: 'Tenacity Turf Herbicide 8 oz',
      affiliateUrl: amazonUrl('B005DUTNF0'),
      price: '62.06',
      description: 'Pairs perfectly with MSO for pre- and post-emergent weed control on cool-season lawns.',
      insertAfterH2: 4,
      imageUrl: amazonImg('7150ZVCDOfL'),
    },
    {
      name: 'Ortho Dial N Spray Hose-End Sprayer (14 Dilution Settings)',
      affiliateUrl: amazonUrl('B0071D0EZK'),
      price: '15.97',
      description: 'MSO goes in a tank mix that you spray. A clean hose-end or pump sprayer delivers the even foliar coverage these mixes need.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41H+XAMwUxS'),
      badge: { label: 'To Spray Your Mix', tone: 'slate' },
    },
  ],

  'scotts-weed-and-feed-application-guide-best-products': [
    {
      // 2026-07-15 CTR pass: the site's #1 traffic post had spreader/liquid/spot-treat
      // support cards but never showed the product the guide is about. Lead card added;
      // ASIN/price/image reused from the buyers-guide entry (PAAPI-verified there).
      name: 'Scotts Turf Builder Weed and Feed 3',
      affiliateUrl: amazonUrl('B00XJM8KMQ'),
      price: '64.99',
      description: 'The bag this application guide is written around: a 2,4-D + MCPP blend that feeds the lawn while it kills dandelions and clover. Spread it dry on a dewy lawn once soil temps hold 55F or higher.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41IVt6FtH2L'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts Weed & Feed 3',
    },
    {
      name: 'T-Zone SE Broadleaf Herbicide',
      affiliateUrl: amazonUrl('B00NWSIHCK'),
      price: '38.12',
      description: 'Pro-grade alternative to weed and feed. Targets tough broadleaf weeds with faster visible results.',
      insertAfterH2: 3,
      imageUrl: amazonImg('61Y+uzC4D+L'),
    },
    {
      name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      affiliateUrl: amazonUrl('B002YPS1KK'),
      price: '52.99',
      description: 'The spreader setting printed on every Scotts weed and feed bag assumes a real broadcast spreader. The EdgeGuard Mini lays product down evenly and shuts off at edges.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31WEgJaq8FL'),
      badge: { label: 'Spreader You Will Need', tone: 'slate' },
    },
    {
      // 2026-08-18 audit: Dial N Spray earned zero clicks on any surface, ever.
      // Celsius (verified ASIN from the bermuda weed-control entry) fills the
      // warm-season spot-spray gap for weeds a weed-and-feed pass misses in heat.
      name: 'Celsius WG Herbicide (Easy Mix Packet)',
      affiliateUrl: amazonUrl('B0BTN1DPMD'),
      price: '15.20',
      description: 'For the weeds still standing after a weed and feed pass in summer heat: selective control of 150+ weeds, labeled for warm-season lawns, with no temperature ceiling on the herbicide itself; its label only bars a spray adjuvant above 90°F.',
      insertAfterH2: 4,
      imageUrl: amazonImg('41hwVWk83-L'),
      badge: { label: 'Summer Spot-Spray', tone: 'green' },
      shortName: 'Celsius WG',
    },
    {
      // 2026-07-11: added the accessible ready-to-spray post-emergent to the site's
      // highest-traffic weed page. Placed at the "spot treat remaining weeds"
      // troubleshooting section (H2 7), distinct from the pro-grade T-Zone at H2 3.
      // Ortho converts ~50% in the Associates report vs T-Zone's ~27%.
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'The no-mix option for broadleaf weeds still standing after a weed and feed pass. It screws onto a garden hose and kills dandelion, clover, and crabgrass across 5,000 sq ft with no separate sprayer.',
      insertAfterH2: 7,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'For Weeds Still Standing', tone: 'orange' },
    },
  ],

  'scotts-turf-builder-halts-crabgrass-preventer-instructions': [
    {
      name: 'Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.',
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: 'The product this guide covers. A spring pre-emergent that stops crabgrass and other listed grassy weeds before they germinate, with a feeding that thickens the lawn in the same pass.',
      insertAfterH2: 1,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: 'The Product', tone: 'gold' },
    },
    {
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'For crabgrass that already broke through, since a pre-emergent only works before seeds sprout. A ready-to-spray hose-end killer that takes down emerged crabgrass, dandelion, and clover.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'For Crabgrass Already Up', tone: 'orange' },
    },
  ],

  'pennington-smart-seed-planting-guide-step-by-step-instructions': [
    {
      name: 'Pennington Smart Seed Sun & Shade Mix South, Tall Fescue (20 lb)',
      affiliateUrl: amazonUrl('B08QCNV9ZV'),
      price: '60.73',
      description: 'The most versatile Smart Seed blend for a typical residential lawn. Tall fescue that handles sun and light shade, with the water-saving coating that holds moisture through the critical first two weeks this guide describes.',
      insertAfterH2: 1,
      imageUrl: amazonImg('81NLk-gC4YS'),
      badge: { label: 'Most Versatile Blend', tone: 'gold' },
    },
  ],

  'slit-seeder-rental-the-complete-guide': [
    {
      name: 'Scotts Turf Builder Rapid Grass Tall Fescue Mix, 16 lb',
      affiliateUrl: amazonUrl('B08KH22BRW'),
      price: '64.10',
      description: 'Tall fescue seed plus starter fertilizer in one bag. Sized for a real overseeding pass (16 lb covers up to ~8,000 sq ft at overseed rates) — matches what most slit-seeder renters need.',
      insertAfterH2: 1,
      imageUrl: amazonImg('51zWGib7hTS'),
      badge: { label: 'Best seed for overseeding', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'REOTEMP Waterproof Soil Thermometer, 5" Stem',
      affiliateUrl: amazonUrl('B07F93K8ZY'),
      price: '12.99',
      description: 'Slit-seed cool-season grass when soil hits 50-55°F. Stainless steel stem reads at 2-4" depth — air temp alone misleads on shoulder seasons.',
      insertAfterH2: 2,
      imageUrl: amazonImg('21ZMzIf-j4L'),
      badge: { label: 'Timing Tool', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder Starter Food for New Grass, 15 lb',
      affiliateUrl: amazonUrl('B00DK49DVA'),
      price: '34.97',
      description: 'High-phosphorus starter fertilizer for slit-seeded turf. Apply over the freshly seeded slits — P drives root establishment in the first 3 weeks.',
      insertAfterH2: 6,
      imageUrl: amazonImg('81oY9zpw+qL'),
      badge: { label: 'Best Companion', tone: 'green' },
    },
  ],

  'when-to-aerate-lawn-timing-guide': [
    {
      name: 'Sun Joe 24V Cordless Scarifier & Dethatcher',
      affiliateUrl: amazonUrl('B08ZDN3NM3'),
      price: '299.99',
      description: 'Cordless dethatcher with 5 depth positions. Pairs with aeration for maximum thatch removal and root zone improvement.',
      insertAfterH2: 2,
      imageUrl: amazonImg('61N-jyLV1EL'),
    },
  ],


  'lawn-care-cost-breakdown-2026': [
    {
      name: 'Simple Lawn Solutions 28-0-0 Liquid Fertilizer (32 oz)',
      affiliateUrl: amazonUrl('B06X9Z9MQR'),
      price: '24.79',
      description: 'High-nitrogen liquid lawn food with quick and slow release blend. Covers 3,200 sq ft per bottle with the attached sprayer. Great value vs premium brands.',
      insertAfterH2: 3,
      imageUrl: amazonImg('61vVfss1f4L'),
    },
  ],

  'cool-season-vs-warm-season-grass-guide': [
    {
      name: 'NightShift Kentucky Bluegrass Seed 25 lb',
      affiliateUrl: amazonUrl('B0D9L9Z15K'),
      price: '215.00',
      description: 'Premium KBG seed for thick, self-repairing cool-season lawns. Top choice for northern climates.',
      insertAfterH2: 2,
      imageUrl: amazonImg('71WkeJOAfdL'),
    },
    {
      name: 'Dark Matter 21-0-0 Ammonium Sulfate',
      affiliateUrl: amazonUrl('B0DZ327Z13'),
      price: '59.99',
      description: 'Fast-acting nitrogen for warm-season lawns that need quick green-up. 40 lb bag with added iron.',
      insertAfterH2: 4,
      imageUrl: amazonImg('61G9WvmlO5L'),
    },
  ],

  'soil-temperature-guide-seed-fertilize-timing': [
    {
      name: 'Tenacity Turf Herbicide 8 oz',
      affiliateUrl: amazonUrl('B005DUTNF0'),
      price: '62.06',
      description: 'Apply at seeding time as a pre-emergent to protect new grass from crabgrass competition. Safe on cool-season lawns.',
      insertAfterH2: 3,
      imageUrl: amazonImg('7150ZVCDOfL'),
    },
  ],


  'diy-lawn-pest-control-safe-options-that-actually-work': [
    {
      name: 'Scotts GrubEx1 Season Long Grub Killer (14.35 lb)',
      affiliateUrl: amazonUrl('B0050DV4ZW'),
      price: '29.47',
      description: 'The #1 grub killer. One application prevents and kills grubs for up to 4 months. Covers 5,000 sq ft. Safe for pets when watered in.',
      insertAfterH2: 2,
      imageUrl: amazonImg('81WYeL+smbL'),
    },
  ],

  'creeping-charlie-identification-removal-guide': [
    {
      name: 'T-Zone SE Broadleaf Herbicide',
      affiliateUrl: amazonUrl('B00NWSIHCK'),
      price: '38.12',
      description: 'One of the most effective herbicides for creeping charlie (ground ivy). Fast visual results on tough broadleaf weeds.',
      insertAfterH2: 3,
      imageUrl: amazonImg('61Y+uzC4D+L'),
    },
  ],

  'brown-patch-prevention': [
    {
      name: 'GS Plant Foods Liquid Kelp Fertilizer',
      affiliateUrl: amazonUrl('B089HSJQ4Q'),
      price: '39.95',
      description: 'Sea kelp strengthens plant cell walls and improves stress tolerance, helping lawns resist fungal pressure naturally.',
      insertAfterH2: 3,
      imageUrl: amazonImg('71F62TboR3L'),
    },
  ],




  // Smart-irrigation "worth it" buying-intent post (GSC: ~3,850 impressions/quarter
  // at avg position ~8.7). Had NO curated entry, so it fell through to the off-intent
  // keyword fallback (surfaced a battery sprayer, a manual edger, and a dethatcher).
  // These two picks are exactly what the article recommends throughout -- a retrofit
  // smart controller plus an optional soil sensor -- and adding them suppresses that
  // fallback. ASINs/prices reused from the watering-schedule + smart-lawn-care-technology-guide
  // siblings (PAAPI-verified; file prices refreshed 2026-07-12). Callouts own the affiliate
  // links; the in-content decision table is intentionally link-free (one product display
  // per section).
  'smart-irrigation-systems-are-they-worth-it': [
    {
      name: 'Rachio 3 Smart Sprinkler Controller 8-Zone',
      affiliateUrl: amazonUrl('B07CZ864Y9'),
      price: '159.00',
      description: 'The retrofit this guide keeps pointing to: swap it for your old wall timer, connect Wi-Fi, and it skips watering before rain and trims run times in cool weather. Weather-adaptive scheduling, app control, and Alexa/Google/HomeKit support.',
      insertAfterH2: 2,
      imageUrl: amazonImg('71asdFrVrlL'),
      badge: { label: 'Editor’s pick for most lawns', tone: 'gold', tier: 'editor' },
    },
    {
      name: 'RAINPOINT 3-in-1 WiFi Soil Moisture Sensor',
      affiliateUrl: amazonUrl('B0D9PW8Q2H'),
      price: '32.99',
      description: 'The cheapest way to make a smart controller genuinely smart: it reads soil moisture at the root zone and sends phone alerts, so watering follows the ground instead of a regional weather station.',
      insertAfterH2: 4,
      imageUrl: amazonImg('61SxfjfDGlL'),
      badge: { label: 'Best budget add-on', tone: 'green', tier: 'pick' },
    },
  ],

  // Commercial-intent curated picks (insertAfterH2: 1) reusing verified sibling ASINs.
  // Weed-control leads are chosen for grass-safety (quinclorac/T-Zone are labeled safe on
  // the target grass) — value the blind keyword fallback can't guarantee.
  // 2026-08-18 audit: the buyers guide's configured editor card duplicated the
  // hand-authored grid's Weed and Feed 3 within one screen (two contradictory
  // gold badges, 2.6x CTR penalty on the duplicate) and misstated the chemistry
  // (MCPP IS mecoprop; the blend is 2,4-D + MCPP + dicamba). Removed — the
  // hand grid is the canonical display and suppresses the category fallback.

  // Cards match the article's actual ranked picks (Celsius, prodiamine, quinclorac,
  // Certainty, 3-way mix). ASINs/prices/images PAAPI-verified in-stock 2026-07-15.
  'best-weed-control-bermuda-grass-safe-products-wont-kill': [
    {
      name: 'Celsius WG Herbicide (Easy Mix Packet)',
      affiliateUrl: amazonUrl('B0BTN1DPMD'),
      price: '15.20',
      description: 'The number one overall pick. Selective post-emergent control of 150+ weeds, labeled for Bermuda, with no temperature ceiling on the herbicide itself; the only heat rule on its label is no spray adjuvant above 90°F.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41hwVWk83-L'),
      badge: { label: 'Best Overall for Bermuda', tone: 'gold', tier: 'editor' },
      shortName: 'Celsius WG',
    },
    {
      name: 'Prodiamine 65 WDG (Generic Barricade)',
      affiliateUrl: amazonUrl('B015BSNZP2'),
      price: '77.00',
      description: 'The pre-emergent workhorse. One spring application stops crabgrass and poa annua before they sprout, safe on established Bermuda at label rates.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41YJyZ6Jo5L'),
      badge: { label: 'Best Pre-Emergent', tone: 'blue' },
      shortName: 'Prodiamine 65 WDG',
    },
    {
      name: 'BASF Drive XLR8 Crabgrass Killer',
      affiliateUrl: amazonUrl('B0058W42QS'),
      price: '55.48',
      description: 'The crabgrass rescue. Liquid quinclorac knocks down crabgrass that already broke through without harming Bermuda at labeled rates.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41sfNjwp+0L'),
      badge: { label: 'Best Crabgrass Rescue', tone: 'green' },
      shortName: 'Drive XLR8',
    },
    {
      // 2026-08-18 audit: Certainty ($132.13) converted 0.35% in this slot —
      // Sedgehammer does the same homeowner nutsedge job at $13.99 (ASIN/price/
      // image reused from the verified zoysia weed-control entry).
      name: 'Sedgehammer Plus Turf Solution',
      affiliateUrl: amazonUrl('B007PHMAYK'),
      price: '13.99',
      description: 'The sedge specialist. Selective halosulfuron clears yellow and purple nutsedge and kyllinga without harming Bermuda, and one box treats a typical lawn several times over.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51w5G6PEFFL'),
      badge: { label: 'Best for Sedges', tone: 'purple' },
      shortName: 'Sedgehammer',
    },
    {
      // 2026-08-18 audit: Trimec (0 clicks, $82.88 badged "budget", and the page
      // itself vetoes 2,4-D in summer heat) → PGF Complete, which the article's
      // own feed-after-weeds nitrogen prescription argues for and nothing sold.
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'Weed control only wins if the Bermuda fills back in. A balanced 16-4-8 with humic DG feeds the recovery this guide prescribes after the weeds are gone, without surging top growth.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Feed the Comeback', tone: 'green' },
      shortName: 'PGF Complete',
    },
  ],

  // The article names Celsius six times as the zoysia-safe answer and had no
  // Celsius card, so it leads here (insertAfterH2: 0 makes it the lead pick,
  // anchored above H2 #1). Ortho/Sedgehammer keep sections 1 and 2 — one
  // product display per section. Metadata PAAPI-verified in-stock 2026-08-26.
  'best-weed-control-zoysia-grass': [
    {
      name: 'Celsius WG Herbicide (Easy Mix Packet)',
      affiliateUrl: amazonUrl('B0BTN1DPMD'),
      price: '15.20',
      description: 'The herbicide this guide keeps coming back to. Selective post-emergent control of 150+ broadleaf and grassy weeds, labeled safe on Zoysia, and it holds up in summer heat when 2,4-D blends would injure the lawn.',
      insertAfterH2: 0,
      imageUrl: amazonImg('41hwVWk83-L'),
      badge: { label: 'Best for Zoysia-Safe Weed Control', tone: 'gold', tier: 'editor' },
      shortName: 'Celsius WG',
    },
    {
      name: 'Ortho WeedClear Weed Killer for Lawns Concentrate (1 gal)',
      affiliateUrl: amazonUrl('B071LH73XJ'),
      price: '36.32',
      description: 'A lawn-safe concentrate labeled for Zoysia that clears broadleaf weeds and grassy invaders like crabgrass in one pass. Mix per label, spot-treat heavy clumps, and keep it off newly seeded areas.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41xa2NAgRFL'),
      badge: { label: 'Best Broadleaf Concentrate', tone: 'blue' },
    },
    {
      name: 'Sedgehammer Plus Turf Solution',
      affiliateUrl: amazonUrl('B007PHMAYK'),
      price: '13.99',
      description: 'Nutsedge is a common Zoysia invader that shrugs off regular weed killers. Selective halosulfuron kills yellow and purple nutsedge without harming the lawn.',
      insertAfterH2: 2,
      imageUrl: amazonImg('51w5G6PEFFL'),
      badge: { label: 'Best for Nutsedge', tone: 'green' },
    },
  ],

  // PAAPI-verified picks (Creators API, in-stock confirmed) for product-review posts.
  '5-differences-between-ego-vs-greenworks-mower': [
    {
      name: 'EGO POWER+ Self-Propelled Electric Lawn Mower with Select Cut (LM2135SP)',
      affiliateUrl: amazonUrl('B0857KWHHC'),
      price: '749.00',
      description: 'The premium side of this comparison: EGO\'s self-propelled Select Cut mower runs quiet, folds flat for storage, and the 56V 7.5Ah battery handles most yards on one charge. You pay more, but it feels closest to a gas mower.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41UumWSChWL'),
      badge: { label: 'Best for large lawns', tone: 'gold', tier: 'pick' },
    },
    {
      name: 'Greenworks 40V 21" Self-Propelled Cordless Lawn Mower (5.0Ah, Charger Included)',
      affiliateUrl: amazonUrl('B0CLSDLM57'),
      price: '465.99',
      description: 'The value side: a 21-inch self-propelled brushless mower on the 40V platform that shares batteries with 75+ Greenworks tools, for roughly $200 less than the comparable EGO.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41FgBiNskDL'),
      badge: { label: 'Best Value', tone: 'blue' },
    },
  ],

  // Flagship battery-mower buying guide (high commercial intent). Single curated LEAD
  // pick — EGO, the post's "best overall" — so a hand-vetted card renders ABOVE H2 #1 at
  // peak buying intent instead of a generic category match. The body's per-product H3s +
  // comparison table already surface Greenworks/Ryobi, so one display keeps sections clean
  // (one-product-display rule). ASIN/price/image reuse the PAAPI-verified ego-vs-greenworks entry.
  'best-battery-lawn-mower-2026': [
    {
      name: 'EGO POWER+ Self-Propelled Electric Lawn Mower with Select Cut (LM2135SP)',
      affiliateUrl: amazonUrl('B0857KWHHC'),
      price: '749.00',
      description: 'Our best-overall pick for medium to large lawns: the 56V platform and included 7.5Ah battery power through thick, damp grass in one pass, and EGO backs it with the longest tool warranty of the mainstream cordless brands.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41UumWSChWL'),
      badge: { label: 'Best overall', tone: 'gold', tier: 'editor' },
      shortName: 'EGO Select Cut',
    },
  ],

  // EGO-vs-Ryobi comparison (high commercial intent). Curated LEAD = EGO, the post's
  // premium/higher-power side, so a hand-vetted card lands ABOVE H2 #1. Reuses the same
  // PAAPI-verified EGO ASIN/image the post already links inline.
  'ego-vs-ryobi-battery-lawn-tools': [
    {
      name: 'EGO POWER+ Self-Propelled Electric Lawn Mower with Select Cut (LM2135SP)',
      affiliateUrl: amazonUrl('B0857KWHHC'),
      price: '749.00',
      description: 'The higher-power side of this matchup: EGO\'s 56V Select Cut mower out-muscles Ryobi\'s 40V line in thick or damp grass, runs quiet, and carries the longest tool warranty of the mainstream cordless brands.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41UumWSChWL'),
      badge: { label: 'Best for raw power', tone: 'gold', tier: 'editor' },
      shortName: 'EGO Select Cut',
    },
  ],

  // === Audit curation pass (2026-07-18) ===
  // Posts that named specific products in-content but relied on the generic fallback card.
  // Every ASIN/image/price below is reused verbatim from a PAAPI-verified sibling entry in
  // THIS file (in-stock as of the sibling's verify date) — nothing minted. best-crabgrass is a
  // genuine multi-product roundup (post-em / pre-em / at-seeding); the rest are single leads.
  'best-crabgrass-killer-top-products-diy-methods': [
    {
      name: 'BASF Drive XLR8 Crabgrass Killer',
      affiliateUrl: amazonUrl('B0058W42QS'),
      price: '55.48',
      description: 'Liquid quinclorac is the most reliable post-emergent for crabgrass that has already broken through, clearing young and mature clumps without harming most established lawn grasses at label rates.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41sfNjwp+0L'),
      badge: { label: 'Best Post-Emergent (Quinclorac)', tone: 'gold', tier: 'editor' },
      shortName: 'Drive XLR8',
    },
    {
      name: 'Prodiamine 65 WDG (Generic Barricade)',
      affiliateUrl: amazonUrl('B015BSNZP2'),
      price: '77.00',
      description: 'A single spring application of prodiamine lays down a pre-emergent barrier that stops crabgrass and poa annua before they germinate, the cheapest way to beat crabgrass long term.',
      insertAfterH2: 2,
      imageUrl: amazonImg('41YJyZ6Jo5L'),
      badge: { label: 'Best Pre-Emergent', tone: 'blue' },
      shortName: 'Prodiamine 65 WDG',
    },
    {
      name: 'Tenacity Turf Herbicide 8 oz',
      affiliateUrl: amazonUrl('B005DUTNF0'),
      price: '62.06',
      description: 'Mesotrione bleaches and kills crabgrass plus 40+ other weeds, and is one of the rare selective herbicides labeled safe to apply at seeding, so it fits new or renovated lawns.',
      insertAfterH2: 3,
      imageUrl: amazonImg('7150ZVCDOfL'),
      badge: { label: 'Best at Seeding (Mesotrione)', tone: 'green' },
      shortName: 'Tenacity',
    },
  ],
  'how-to-get-rid-of-doveweed': [
    {
      name: 'Celsius WG Herbicide (Easy Mix Packet)',
      affiliateUrl: amazonUrl('B0BTN1DPMD'),
      price: '15.20',
      description: 'Doveweed shrugs off most common lawn herbicides, but Celsius WG controls it and 150+ other weeds and stays safe on warm-season grasses like St. Augustine, Bermuda, and Zoysia even in summer heat.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41hwVWk83-L'),
      badge: { label: 'Best for Doveweed', tone: 'green' },
      shortName: 'Celsius WG',
    },
  ],
  'how-to-get-rid-of-dollarweed': [
    {
      name: 'Southern Ag Atrazine Weed Killer for St. Augustine & Centipede (32 oz)',
      affiliateUrl: amazonUrl('B0052MZBYQ'),
      price: '16.49',
      description: 'Dollarweed thrives in wet, low-mowed turf, and on St. Augustine or Centipede lawns atrazine controls it as both a pre- and post-emergent at about one quart per 3,700 sq ft.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41xJ0HypHcL'),
      badge: { label: 'Best for Southern Lawns', tone: 'green' },
      shortName: 'Southern Ag Atrazine',
    },
  ],
  'spring-lawn-fertilizer-schedule-apply-grass-type': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 with 7% humic DG and super-fine particles that spread evenly and feed steadily, a reliable all-around choice for a spring feeding on most grass types.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best All-Around Granular', tone: 'gold', tier: 'editor' },
      shortName: 'Andersons PGF Complete',
    },
  ],

  'bioadvanced-bayer-grub-killer-instructions-apply': [
    {
      name: 'BioAdvanced Season Long Grub Control Granules (10 lb)',
      affiliateUrl: amazonUrl('B09K95XHY4'),
      price: '24.99',
      description: 'The product this guide walks you through applying: one granular treatment creates a season-long protective zone against grubs, mole crickets, and chinch bugs. Spread it dry and water it in per the timing below.',
      insertAfterH2: 1,
      imageUrl: amazonImg('516l9zVfE+L'),
      badge: { label: 'Best season-long grub control', tone: 'gold', tier: 'editor' },
    },
  ],

  'best-natural-weed-killer-top-products-diy-methods': [
    {
      name: 'Natural Armor All-Natural Weed & Grass Killer, Glyphosate-Free (1 Gallon)',
      affiliateUrl: amazonUrl('B07R54443W'),
      price: '28.95',
      description: 'A ready-to-use, glyphosate-free concentrate for driveways, patios, and fence lines where you want nothing growing. Non-selective, so keep it off the lawn you want to keep.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41iZ62PVH5L'),
      badge: { label: 'Best for paths & fence lines', tone: 'gold', tier: 'editor' },
    },
  ],

  'best-weed-feed-bermuda-grass-2026-guide': [
    {
      name: 'BioAdvanced 3-in-1 Weed and Feed for Southern Lawns (10 lb)',
      affiliateUrl: amazonUrl('B0BR8J59CL'),
      price: '30.98',
      description: 'A southern-lawn weed and feed whose 2,4-D + quinclorac + dicamba blend is labeled for Bermuda: it feeds the grass while clearing broadleaf weeds and crabgrass in one granular pass.',
      insertAfterH2: 1,
      imageUrl: amazonImg('512ZbA96MxL'),
      badge: { label: 'Best weed & feed for Bermuda', tone: 'gold', tier: 'editor' },
    },
    // Carried over from the retired best-weed-feed-bermuda-grass post when it
    // was 301'd into this one (2026-08-24); lands in the resistant-weeds section.
    {
      name: 'Ortho WeedClear Lawn Weed Killer Ready-To-Spray (32 oz, covers 5,000 sq ft)',
      affiliateUrl: amazonUrl('B07YM583XL'),
      price: '9.49',
      description: 'For the persistent weeds this section covers: a bermuda-safe spot treatment that picks off survivors between weed and feed passes instead of re-blanketing the lawn.',
      insertAfterH2: 5,
      imageUrl: amazonImg('31HhdFzoDsL'),
      badge: { label: 'Spot-Treat Survivors', tone: 'blue' },
    },
  ],

  // === Blog-audit monetization batch (2026-08-26) ===
  // 5,500-word buying-intent grub guide had zero product links in the body.
  // Both ASINs/prices/images reused from PAAPI-verified entries in this file;
  // each pick maps to the active ingredient its target section names.
  'how-to-control-grubs-in-your-lawn': [
    {
      name: 'Scotts GrubEx1 Season Long Grub Killer, 5,000 sq. ft.',
      affiliateUrl: amazonUrl('B0050DV4ZW'),
      price: '29.47',
      description: 'The chlorantraniliprole preventer this section recommends: one spring application in the late-April to early-June window protects roots all season.',
      insertAfterH2: 3,
      imageUrl: amazonImg('41vLRIqeMiL'),
      badge: { label: 'Best preventive grub control', tone: 'gold', tier: 'editor' },
      shortName: 'GrubEx',
    },
    {
      name: 'BioAdvanced Season Long Grub Control Granules (10 lb)',
      affiliateUrl: amazonUrl('B09K95XHY4'),
      price: '24.99',
      description: 'The imidacloprid option for the early-summer preventive window: spread dry on the seasonal schedule this plan lays out, then water it in.',
      insertAfterH2: 5,
      imageUrl: amazonImg('516l9zVfE+L'),
      badge: { label: 'Season-long alternative', tone: 'green', tier: 'pick' },
      shortName: 'BioAdvanced Grub Control',
    },
  ],

  // Hardscape pages: monetize the install consumables, not the pavers/stone
  // (those are bought locally; a paver ASIN would be a low-CTR mismatch).
  // ASINs PAAPI-verified 2026-08-26 via paapi-asin-lookup.
  'pathway-stepping-stone-ideas-for-lawns': [
    {
      name: 'Dewitt Pro-5 Weed Barrier Fabric 5 oz, 4 x 250 Feet',
      affiliateUrl: amazonUrl('B000W6900S'),
      price: '119.99',
      description: 'The woven geotextile the material section calls for under pavers and stone: 5 oz commercial grade, so the path base stays weed-free for years.',
      insertAfterH2: 3,
      imageUrl: amazonImg('31WJB9W8G4L'),
      badge: { label: 'Path base weed barrier', tone: 'green', tier: 'pick' },
      shortName: 'DeWitt Pro-5 Fabric',
    },
    {
      name: 'Ashman 200 Pack Galvanized Garden Stakes Landscape Staples (6 inch)',
      affiliateUrl: amazonUrl('B01EWH0VWS'),
      price: '22.99',
      description: 'For the install steps: 6-inch galvanized staples that pin the barrier fabric flat before the base layer goes down.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51wNWuVK8hL'),
      badge: { label: 'Pins the fabric flat', tone: 'blue', tier: 'pick' },
      shortName: 'Ashman Staples',
    },
  ],
  'creative-lawn-edging-ideas': [
    {
      name: 'EasyFlex No-Dig Landscape Edging with Anchoring Spikes, 100 Foot Kit',
      affiliateUrl: amazonUrl('B09KH9KP6M'),
      price: '64.70',
      description: 'The no-dig plastic edging from the comparison: a 100-foot kit with 72 anchoring spikes, installed with a mallet instead of a trench.',
      insertAfterH2: 1,
      imageUrl: amazonImg('41gMi3ByVyL'),
      badge: { label: 'Easiest DIY install', tone: 'green', tier: 'pick' },
      shortName: 'EasyFlex Edging Kit',
    },
    {
      name: 'Colwelt Saw-Tooth Half Moon Lawn Edger with T-Grip, 38-Inch',
      affiliateUrl: amazonUrl('B0972CDQYL'),
      price: '26.59',
      description: 'The half-moon step edger the install section names for cutting a clean V-trench edge by hand.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51l3-w9-PyL'),
      badge: { label: 'For the V-trench edge', tone: 'blue', tier: 'pick' },
      shortName: 'Colwelt Half-Moon Edger',
    },
  ],

  // === types-of-grass-in-* state pages (2026-08-26 monetization batch) ===
  // Seed/establishment pick after H2 2 ("{State} grass types compared at a
  // glance"), second pick after H2 5 ("{State} grass timing that matters").
  // Species match each article's own extension-sourced recommendation (no
  // warm-season products on WA/WI, plugs not seed for St. Augustine states).
  // All ASINs PAAPI-verified in stock with current prices on 2026-08-26.
  //
  // The second slot is a spring pre-emergent (Scotts Halts), so it carries a
  // `seasonWindow` of Feb-Apr and an `offSeasonAlternate`: the cool-season pages
  // fall back to Scotts Turf Builder Lawn Food, the warm-season ones (OK, AR, TX,
  // FL) to Scotts Turf Builder Southern Lawn Food. See lib/seasonal-picks.
  'types-of-grass-in-nebraska': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A Kentucky bluegrass blend for the statewide UNL pick this article recommends, seeded in the late-summer window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Nebraska bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The granular crabgrass pre-emergent this article calls for in spring, before Nebraska soil warms enough for crabgrass to germinate.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Nebraska reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Nebraska, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-iowa': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A Kentucky bluegrass blend that matches the default Iowa lawn grass, sown inside the Iowa State late-summer seeding window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Iowa bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular pre-emergent for the spring crabgrass window this article keeps returning to, applied before germination starts.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Crabgrass prevention pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Iowa reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Iowa, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-illinois': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A bluegrass blend for the number one Illinois lawn grass named here, best sown in the late-summer seeding window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Illinois bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The spring pre-emergent this article recommends putting down before crabgrass germinates across Illinois.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Illinois reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Illinois, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-kentucky': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A Kentucky bluegrass blend for the grass this article names first, seeded in the fall window rather than in spring.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Kentucky bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular crabgrass pre-emergent for the spring timing this article flags, applied before soil temperatures climb.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Kentucky reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Kentucky, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-wisconsin': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A cool-season Kentucky bluegrass blend that matches the UW Extension lineup in this article, with no warm-season grass involved.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Wisconsin bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The spring crabgrass pre-emergent this article mentions, timed to Wisconsin soil warming rather than to a calendar date.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Wisconsin reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Wisconsin, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-minnesota': [
    {
      name: "Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B07SGL71PP'),
      price: '37.95',
      description: "A Kentucky bluegrass blend for the cool-season lineup this article leads with, sown in the late-summer Minnesota seeding window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41bn4mXqXVL'),
      badge: { label: "Minnesota bluegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Heisman KBG Blend",
    },
    {
      name: "Scotts Turf Builder Starter Food for New Grass, 15 lb.",
      affiliateUrl: amazonUrl('B00DK49DVA'),
      price: '34.97',
      description: "A phosphorus-rich starter fertilizer to put down with seed during the late-summer seeding window this article describes.",
      insertAfterH2: 5,
      imageUrl: amazonImg('51su2w24uqL'),
      badge: { label: "Seeding-time feed pick", tone: 'purple', tier: 'pick' },
      shortName: "Scotts Starter Food",
    },
  ],
  'types-of-grass-in-kansas': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A deep-rooted turf-type tall fescue blend for the transition-zone default this article recommends, sown in the September window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Kansas tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular crabgrass pre-emergent for the mid-April timing this article gives for Kansas lawns.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "April pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Kansas reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Kansas, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-new-jersey': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A tall fescue and bluegrass blend that matches the mixed cool-season lawns this article describes, sown in the Rutgers seeding window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "New Jersey blend pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The spring crabgrass pre-emergent this article calls for, applied before New Jersey soil reaches germination temperature.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a New Jersey reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in New Jersey, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-pennsylvania': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A bluegrass, fescue, and ryegrass style blend for the cool-season lawns this article covers, sown in the late-summer window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Pennsylvania blend pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular pre-emergent for the spring crabgrass timing this article returns to across Pennsylvania.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Pennsylvania reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Pennsylvania, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-maryland': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A turf-type tall fescue blend matching the UMD Extension recommendation this article makes for most Maryland homes.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Maryland tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "MySoil Soil Test Kit",
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: "A mail-in soil test that answers the pH and nutrient question this article says to settle before you fertilize or seed.",
      insertAfterH2: 5,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: "Test before you feed", tone: 'slate', tier: 'pick' },
      shortName: "MySoil Test Kit",
    },
  ],
  'types-of-grass-in-indiana': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A cool-season tall fescue and bluegrass blend for the seed mixes this article describes, sown in the late-summer Indiana window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Indiana blend pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A spring crabgrass pre-emergent for the timing section above, applied before Indiana soil warms into the germination range.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Indiana reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Indiana, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-ohio': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A cool-season blend for the OSU seed mixes and overseeding this article walks through, sown in the late-summer window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Ohio overseeding pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The granular crabgrass pre-emergent this article points to for the spring window in Ohio.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Ohio reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Ohio, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-michigan': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A bluegrass, ryegrass, and fescue style blend for the August 15 to September 15 seeding window this article gives.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Michigan seeding pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular pre-emergent to put down before Michigan soil crosses the 50 to 55 degree crabgrass trigger this article names.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Soil-temp pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Michigan reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Michigan, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-missouri': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A turf-type tall fescue blend for the transition-zone default this article leads with, sown in the fall Missouri window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Missouri tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A spring crabgrass pre-emergent for the timing this article describes, applied before Missouri soil warms.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Missouri reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Missouri, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-virginia': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A turf-type tall fescue blend for the dominant cool-season choice this article makes across most of Virginia, sown in fall.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Virginia tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "The spring crabgrass pre-emergent this article calls for before Virginia soil reaches germination temperature.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a Virginia reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in Virginia, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-north-carolina': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A turf-type tall fescue blend for the cool-season side of the fescue versus bermuda fork this article lays out, sown in fall.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Piedmont tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular pre-emergent for the spring crabgrass timing this article gives for North Carolina lawns.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // fall feed, the thing a North Carolina reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Lawn Food",
        affiliateUrl: amazonUrl('B005W1QXQW'),
        price: '21.79',
        description: "The all-grass-type granular feed for the cool-season lawns this article names. Fall is the application that matters most in North Carolina, going down while the grass is still growing so it builds roots ahead of winter.",
        imageUrl: amazonImg('41-eKeqethL'),
        badge: { label: "Fall feeding pick", tone: 'purple', tier: 'pick' },
        shortName: "Scotts Turf Builder",
      },
    },
  ],
  'types-of-grass-in-washington': [
    {
      name: "Outsidepride Fireball & Hattrick Perennial Ryegrass Blend (5 lb)",
      affiliateUrl: amazonUrl('B0040B6SO2'),
      price: '29.99',
      description: "An elite perennial ryegrass blend for the ryegrass and fine fescue lawns this article recommends west of the Cascades.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61JMOwnTsuL'),
      badge: { label: "Westside ryegrass pick", tone: 'green', tier: 'pick' },
      shortName: "Fireball & Hattrick Rye",
    },
    {
      name: "MySoil Soil Test Kit",
      affiliateUrl: amazonUrl('B084TSNR79'),
      price: '34.99',
      description: "A mail-in soil test for the acidic western Washington soils this article says to check before you feed or lime.",
      insertAfterH2: 5,
      imageUrl: amazonImg('513zUGwGG8L'),
      badge: { label: "Test before you feed", tone: 'slate', tier: 'pick' },
      shortName: "MySoil Test Kit",
    },
  ],
  'types-of-grass-in-oklahoma': [
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the state default this article leads with, sown in the late-spring window once soil is warm.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Oklahoma bermuda pick", tone: 'green', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular crabgrass pre-emergent for the early spring window this article gives, ahead of Oklahoma soil warming.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // Southern fall feed, the thing a Oklahoma reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Southern Lawn Food",
        affiliateUrl: amazonUrl('B000UGU59K'),
        price: '51.18',
        description: "A Southern-formulated feed for the warm-season lawns this article covers. Applied while Oklahoma turf is still growing, it holds color and builds the reserves the lawn spends over winter.",
        imageUrl: amazonImg('41k2rKURWKL'),
        badge: { label: "Warm-season feeding pick", tone: 'orange', tier: 'pick' },
        shortName: "Scotts Southern",
      },
    },
  ],
  'types-of-grass-in-arkansas': [
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the most common Arkansas lawn grass in this article, sown once soil has warmed in late spring.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Arkansas bermuda pick", tone: 'green', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A crabgrass pre-emergent for the early spring application this article describes for Arkansas lawns.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "Spring pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // Southern fall feed, the thing a Arkansas reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Southern Lawn Food",
        affiliateUrl: amazonUrl('B000UGU59K'),
        price: '51.18',
        description: "A Southern-formulated feed for the warm-season lawns this article covers. Applied while Arkansas turf is still growing, it holds color and builds the reserves the lawn spends over winter.",
        imageUrl: amazonImg('41k2rKURWKL'),
        badge: { label: "Warm-season feeding pick", tone: 'orange', tier: 'pick' },
        shortName: "Scotts Southern",
      },
    },
  ],
  'types-of-grass-in-texas': [
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the most planted Texas lawn grass in this article, sown once soil is reliably warm in late spring.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Texas bermuda pick", tone: 'green', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular crabgrass pre-emergent for the February window this article names, applied before Texas soil crosses 55 degrees.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "February pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // Southern fall feed, the thing a Texas reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Southern Lawn Food",
        affiliateUrl: amazonUrl('B000UGU59K'),
        price: '51.18',
        description: "A Southern-formulated feed for the warm-season lawns this article covers. Applied while Texas turf is still growing, it holds color and builds the reserves the lawn spends over winter.",
        imageUrl: amazonImg('41k2rKURWKL'),
        badge: { label: "Warm-season feeding pick", tone: 'orange', tier: 'pick' },
        shortName: "Scotts Southern",
      },
    },
  ],
  'types-of-grass-in-tennessee': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A turf-type tall fescue blend for the cool-season half of the transition-zone split this article describes, sown in fall.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "East Tennessee fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the warm-season side of this article, sown in the late-spring window rather than the fall one.",
      insertAfterH2: 5,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Warm-season Tennessee pick", tone: 'orange', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
  ],
  'types-of-grass-in-mississippi': [
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the most widely grown Mississippi lawn grass in this article, sown once soil is warm.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Mississippi bermuda pick", tone: 'green', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
    {
      name: "Scotts Turf Builder Southern Lawn Food",
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: "A southern-formulated lawn food for the growing-season feedings this article schedules once warm-season grass is actively growing.",
      insertAfterH2: 5,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: "Warm-season feeding pick", tone: 'purple', tier: 'pick' },
      shortName: "Scotts Southern Lawn Food",
    },
  ],
  'types-of-grass-in-alabama': [
    {
      name: "Scotts Turf Builder Grass Seed Bermudagrass (4 lb)",
      affiliateUrl: amazonUrl('B0B72BDF86'),
      price: '34.97',
      description: "Seeded bermudagrass for the warm-season lineup this article leads with, sown in the late-spring Alabama window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('61fLxvlKUwL'),
      badge: { label: "Alabama bermuda pick", tone: 'green', tier: 'pick' },
      shortName: "Scotts Bermudagrass Seed",
    },
    {
      name: "Scotts Turf Builder Southern Lawn Food",
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: "A southern lawn food for the growing-season feedings this article ties to warm-season green-up in Alabama.",
      insertAfterH2: 5,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: "Warm-season feeding pick", tone: 'purple', tier: 'pick' },
      shortName: "Scotts Southern Lawn Food",
    },
  ],
  'types-of-grass-in-louisiana': [
    {
      name: "St. Augustine Palmetto Grass Plugs (18 Extra-Large Live Plugs)",
      affiliateUrl: amazonUrl('B0976DN86K'),
      price: '59.97',
      description: "Live St. Augustine plugs for the grass this article puts first, which is established from plugs or sod rather than seed.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41P5nbHoovL'),
      badge: { label: "Louisiana St. Augustine pick", tone: 'green', tier: 'pick' },
      shortName: "Palmetto Plugs",
    },
    {
      name: "Scotts Turf Builder Southern Lawn Food",
      affiliateUrl: amazonUrl('B000UGU59K'),
      price: '51.18',
      description: "A southern lawn food for the growing-season feedings this article schedules once Louisiana turf is fully green.",
      insertAfterH2: 5,
      imageUrl: amazonImg('41k2rKURWKL'),
      badge: { label: "Warm-season feeding pick", tone: 'purple', tier: 'pick' },
      shortName: "Scotts Southern Lawn Food",
    },
  ],
  'types-of-grass-in-florida': [
    {
      name: "St. Augustine Palmetto Grass Plugs (18 Extra-Large Live Plugs)",
      affiliateUrl: amazonUrl('B0976DN86K'),
      price: '59.97',
      description: "Live St. Augustine plugs for the statewide default in this article, since St. Augustine is started from plugs or sod and not seed.",
      insertAfterH2: 2,
      imageUrl: amazonImg('41P5nbHoovL'),
      badge: { label: "Florida St. Augustine pick", tone: 'green', tier: 'pick' },
      shortName: "Palmetto Plugs",
    },
    {
      name: "Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.",
      affiliateUrl: amazonUrl('B08L6T3B4H'),
      price: '55.71',
      description: "A granular crabgrass pre-emergent for the February windows this article lists by region, put down before soil warms.",
      insertAfterH2: 5,
      imageUrl: amazonImg('5196bQGYE8L'),
      badge: { label: "February pre-emergent pick", tone: 'blue', tier: 'pick' },
      shortName: "Scotts Halts",
      // Halts is a spring pre-emergent. Outside its window this slot carries the
      // Southern fall feed, the thing a Florida reader can act on now.
      seasonWindow: { startMonth: 2, endMonth: 4 },
      offSeasonAlternate: {
        name: "Scotts Turf Builder Southern Lawn Food",
        affiliateUrl: amazonUrl('B000UGU59K'),
        price: '51.18',
        description: "A Southern-formulated feed for the warm-season lawns this article covers. Applied while Florida turf is still growing, it holds color and builds the reserves the lawn spends over winter.",
        imageUrl: amazonImg('41k2rKURWKL'),
        badge: { label: "Warm-season feeding pick", tone: 'orange', tier: 'pick' },
        shortName: "Scotts Southern",
      },
    },
  ],
  'types-of-grass-in-georgia': [
    {
      name: "TifBlair Centipede Grass Seed (1 lb, covers up to ~4,000 sq ft)",
      affiliateUrl: amazonUrl('B01834WDV8'),
      price: '66.21',
      description: "Improved TifBlair centipede seed for the low-input Georgia lawn grass this article highlights, sown in the warm-season window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('51UJ-pNXKOL'),
      badge: { label: "Georgia centipede pick", tone: 'green', tier: 'pick' },
      shortName: "TifBlair Centipede Seed",
    },
    {
      name: "Ferti-Lome Centipede Lawn Fertilizer 15-0-15 (20 lb)",
      affiliateUrl: amazonUrl('B01LIIWPFK'),
      price: '36.07',
      description: "A low-phosphorus 15-0-15 built for the centipede lawns this article describes, matched to the growing-season feeding timing.",
      insertAfterH2: 5,
      imageUrl: amazonImg('41CRNNyjL+L'),
      badge: { label: "Centipede feeding pick", tone: 'purple', tier: 'pick' },
      shortName: "Ferti-Lome 15-0-15",
    },
  ],
  'types-of-grass-in-south-carolina': [
    {
      name: "TifBlair Centipede Grass Seed (1 lb, covers up to ~4,000 sq ft)",
      affiliateUrl: amazonUrl('B01834WDV8'),
      price: '66.21',
      description: "Improved TifBlair centipede seed for the low-maintenance South Carolina lawn grass this article covers, sown in the warm-season window.",
      insertAfterH2: 2,
      imageUrl: amazonImg('51UJ-pNXKOL'),
      badge: { label: "South Carolina centipede pick", tone: 'green', tier: 'pick' },
      shortName: "TifBlair Centipede Seed",
    },
    {
      name: "Ferti-Lome Centipede Lawn Fertilizer 15-0-15 (20 lb)",
      affiliateUrl: amazonUrl('B01LIIWPFK'),
      price: '36.07',
      description: "A low-phosphorus centipede fertilizer for the light feeding schedule this article recommends for warm-season lawns.",
      insertAfterH2: 5,
      imageUrl: amazonImg('41CRNNyjL+L'),
      badge: { label: "Centipede feeding pick", tone: 'purple', tier: 'pick' },
      shortName: "Ferti-Lome 15-0-15",
    },
  ],
  'types-of-grass-in-colorado': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A deep-rooted turf-type tall fescue blend for the lower-water cool-season option this article puts alongside Kentucky bluegrass.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "Colorado tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
  ],
  'types-of-grass-in-california': [
    {
      name: "Jonathan Green Black Beauty Ultra Grass Seed",
      affiliateUrl: amazonUrl('B003AYWIU8'),
      price: '38.15',
      description: "A drought-tolerant turf-type tall fescue blend for the cool-season California zones this article maps out, sown in fall.",
      insertAfterH2: 2,
      imageUrl: amazonImg('71I5+E4pKxL'),
      badge: { label: "California tall fescue pick", tone: 'green', tier: 'pick' },
      shortName: "Black Beauty Ultra",
    },
  ],
  // === Fall audit 2026-09-12 ===
  // ASINs, images and prices reused from the PAAPI-verified entries above
  // (WinterGuard from the Scotts 4-Step set, PGF Complete from the Florida
  // St. Augustine set). Anchors counted against the 12 H2s in the post after
  // the fall retitle: H2 3 is "Best Fall Lawn Fertilizer: What to Buy Before
  // Winter" and H2 7 follows "When to Apply Fall Fertilizer by Region and
  // Grass Type".
  'best-winter-lawn-fertilizer-complete-guide': [
    {
      name: 'The Andersons PGF Complete 16-4-8 (5,000 sq ft)',
      affiliateUrl: amazonUrl('B07SYLCKRH'),
      price: '59.88',
      description: 'A balanced 16-4-8 for the earlier fall feedings, with humic DG to keep nutrients working in the root zone while the grass is still growing.',
      insertAfterH2: 3,
      shortName: 'Andersons PGF Complete',
      imageUrl: amazonImg('41xelE+7eJL'),
      badge: { label: 'Best Balanced Fall Feed', tone: 'blue' },
    },
    {
      name: 'Scotts Turf Builder WinterGuard Fall Lawn Food, 12.5 lb.',
      affiliateUrl: amazonUrl('B000I1M5OU'),
      price: '39.87',
      description: 'The late-fall bag for cool-season lawns, fed once growth slows so the roots store nutrients before the ground freezes.',
      insertAfterH2: 7,
      imageUrl: amazonImg('51Uon3VMZZL'),
      badge: { label: 'Late-fall winterizer pick', tone: 'purple', tier: 'pick' },
      shortName: 'WinterGuard Fall',
    },
  ],

  // === Fall disease + turf-pest cluster (2026-09-13) ===
  // ASINs, titles, prices and images PAAPI-verified 2026-09-13. Every post in
  // this set already carries an in-content Amazon link in its own prose, so
  // each pick is anchored to a DIFFERENT section than that link (one product
  // display per section) and disableLeadAnchor keeps the earliest pick at its
  // configured boundary instead of snapping it into the intro. H2 indexes
  // counted against the live Directus content (posts 226, 371, 527-531).
  'take-all-root-rot-st-augustine': [
    {
      // H2 #7 "What Other Guides Miss" (H2 #6, the fungicide section, already
      // carries the post's own DiseaseEx link).
      name: 'Scotts DiseaseEx Lawn Fungicide, 10 lbs.',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The azoxystrobin this article calls the right chemistry for take-all. Granular matters here: the pathogen lives at the roots, so you spread it and water it in rather than coating the blades.',
      insertAfterH2: 7,
      imageUrl: amazonImg('51NvPtV0VnL'),
      badge: { label: 'The Right Chemistry', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts DiseaseEx',
      disableLeadAnchor: true,
    },
    {
      // H2 #8 "Your Recovery Plan, In Order"
      name: 'BioAdvanced Fungus Control for Lawns - 32 fl oz Bottle',
      affiliateUrl: amazonUrl('B0035KNZJU'),
      price: '16.98',
      description: 'A propiconazole partner for a second application in the recovery plan. Rotating chemistry keeps the pathogen from adapting across a season-long rebuild.',
      insertAfterH2: 8,
      imageUrl: amazonImg('41i4wnRkw-L'),
      badge: { label: 'Rotation Partner', tone: 'slate' },
      shortName: 'BioAdvanced Fungus Control',
    },
  ],

  'large-patch-lawn-disease-zoysia-st-augustine': [
    {
      // H2 #8 "What Other Guides Miss" (H2 #6, the preventive-window section,
      // already carries the post's own DiseaseEx link).
      name: 'Scotts DiseaseEx Lawn Fungicide, 10 lbs.',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The bag that makes the fall calendar this section argues for actually happen. Azoxystrobin is labeled for large patch, and a granular spread reaches the crowns and leaf sheaths where the fungus works.',
      insertAfterH2: 8,
      imageUrl: amazonImg('51NvPtV0VnL'),
      badge: { label: 'For the Fall Window', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts DiseaseEx',
      disableLeadAnchor: true,
    },
  ],

  'scotts-diseaseex-instructions-how-to-apply': [
    {
      // H2 #5 "How to apply Scotts DiseaseEx step by step" (H2 #1 already
      // carries the post's own product link).
      name: 'Scotts DiseaseEx Lawn Fungicide, 10 lbs.',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The exact bag these instructions are written for. One 10 lb bag covers 5,000 square feet at the preventive rate, so measure the lawn first and buy against that number.',
      insertAfterH2: 5,
      imageUrl: amazonImg('51NvPtV0VnL'),
      badge: { label: 'The Product Covered', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts DiseaseEx',
      disableLeadAnchor: true,
    },
  ],

  'best-fungicide-for-st-augustine-grass': [
    {
      // H2 #4 "Matching the fungicide to the disease" (H2 #3 is the comparison
      // table, which already links both products).
      name: 'Scotts DiseaseEx Lawn Fungicide, 10 lbs.',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The azoxystrobin pick for the preventive half of this matching table: brown patch and large patch, applied before symptoms rather than after.',
      insertAfterH2: 4,
      imageUrl: amazonImg('51NvPtV0VnL'),
      badge: { label: 'Best Preventive', tone: 'gold', tier: 'editor' },
      shortName: 'Scotts DiseaseEx',
      disableLeadAnchor: true,
    },
    {
      // H2 #7 "Your buying plan, in order"
      name: 'BioAdvanced Fungus Control for Lawns - 32 fl oz Bottle',
      affiliateUrl: amazonUrl('B0035KNZJU'),
      price: '16.98',
      description: 'The propiconazole half of the buying plan, for lesions that are already spreading. Systemic, curative, and cheap enough to keep on the shelf before you need it.',
      insertAfterH2: 7,
      imageUrl: amazonImg('41i4wnRkw-L'),
      badge: { label: 'Best Curative', tone: 'green' },
      shortName: 'BioAdvanced Fungus Control',
    },
  ],

  'gray-leaf-spot-lawn-identification-treatment': [
    {
      // H2 #5 "The Cultural Fixes That Actually End It" (H2 #4, the treatment
      // section, already carries the post's own links).
      name: 'BioAdvanced Fungus Control for Lawns - 32 fl oz Bottle',
      affiliateUrl: amazonUrl('B0035KNZJU'),
      price: '16.98',
      description: 'The systemic propiconazole this treatment plan leans on while the cultural changes take hold. It moves into the plant instead of sitting on the blade, which is what an active lesion needs.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41i4wnRkw-L'),
      badge: { label: 'For an Active Outbreak', tone: 'gold', tier: 'editor' },
      shortName: 'BioAdvanced Fungus Control',
      disableLeadAnchor: true,
    },
    {
      // H2 #6 "What Other Guides Miss: The Overseeding Trap"
      name: 'Scotts DiseaseEx Lawn Fungicide, 10 lbs.',
      affiliateUrl: amazonUrl('B075R7BNXV'),
      price: '21.47',
      description: 'The preventive option for the overseeding trap this section describes. Azoxystrobin goes down before ryegrass seedlings meet the fungus, which is the only timing that helps them.',
      insertAfterH2: 6,
      imageUrl: amazonImg('51NvPtV0VnL'),
      badge: { label: 'Preventive Alternative', tone: 'blue' },
      shortName: 'Scotts DiseaseEx',
    },
  ],

  'how-to-get-rid-of-chinch-bugs': [
    {
      // H2 #5 "When to Re-Treat, and When to Switch Chemistry". H2 #1, the
      // product-choice section, already links this ASIN, and H2 #3 is the
      // reminder widget's fixed anchor in data/blog-reminder-widgets.ts.
      name: 'Ortho BugClear Insect Killer for Lawns & Landscapes Ready-to-Spray, 32 oz.',
      affiliateUrl: amazonUrl('B07YM5V2V5'),
      price: '10.98',
      description: 'The hose-end bifenthrin for a second pass on a patch that is still active. It screws onto a garden hose with no mixing, and one bottle covers the damaged area plus the buffer around it.',
      insertAfterH2: 5,
      imageUrl: amazonImg('41QGhrzLBcS'),
      badge: { label: 'Best for a Single Patch', tone: 'gold', tier: 'editor' },
      shortName: 'Ortho BugClear RTS',
      disableLeadAnchor: true,
    },
  ],

  'armyworm-control-how-to-get-rid-of-armyworms-quickly': [
    {
      // H2 #7 "What Other Guides Miss: Common Mistakes to Avoid". H2 #4, the
      // treatment section, already links this ASIN, and H2 #3 is the reminder
      // widget's fixed anchor in data/blog-reminder-widgets.ts.
      name: 'Ortho BugClear Insect Killer for Lawns & Landscapes Ready-to-Spray, 32 oz.',
      affiliateUrl: amazonUrl('B07YM5V2V5'),
      price: '10.98',
      description: 'The bifenthrin ready-to-spray that avoids the rate and timing mistakes this section lists. It kills on contact, and a hose-end bottle needs no measuring on the evening an outbreak is stripping turf.',
      insertAfterH2: 7,
      imageUrl: amazonImg('41QGhrzLBcS'),
      badge: { label: 'The Fast Treatment', tone: 'gold', tier: 'editor' },
      shortName: 'Ortho BugClear RTS',
      disableLeadAnchor: true,
    },
  ],
}
